import React from 'react';

// Brand colors from the site
const colors = {
  primary: '#D5A4AF',      // Dusty rose
  primaryDark: '#C08E99',  // Darker dusty rose
  secondary: '#2d3748',    // Dark slate
  ocean: '#0d9488',        // Teal for maritime
  oceanLight: '#5eead4',   // Light teal
  slate: '#64748b',
  slateLight: '#94a3b8',
  white: '#ffffff',
  rose50: '#F9F3F5',
  rose100: '#F5EAED',
};

// ============================================
// 1. COMPENSATION COMPARISON CHART
// Shows 3.5x more compensation with attorney
// ============================================
export const CompensationComparisonChart = ({ className = '' }) => (
  <svg 
    viewBox="0 0 600 350" 
    className={`w-full max-w-2xl mx-auto ${className}`}
    role="img"
    aria-label="Comparison chart showing compensation with and without attorney representation"
  >
    {/* Background */}
    <rect width="600" height="350" fill={colors.rose50} rx="16" />
    
    {/* Title */}
    <text x="300" y="35" textAnchor="middle" fill={colors.secondary} fontSize="18" fontWeight="bold">
      Average Compensation: With vs. Without an Attorney
    </text>
    
    {/* Y-axis labels */}
    <text x="45" y="280" fill={colors.slate} fontSize="12" textAnchor="end">$0</text>
    <text x="45" y="220" fill={colors.slate} fontSize="12" textAnchor="end">$25K</text>
    <text x="45" y="160" fill={colors.slate} fontSize="12" textAnchor="end">$50K</text>
    <text x="45" y="100" fill={colors.slate} fontSize="12" textAnchor="end">$75K</text>
    
    {/* Grid lines */}
    <line x1="55" y1="280" x2="550" y2="280" stroke={colors.slateLight} strokeWidth="1" strokeDasharray="4" />
    <line x1="55" y1="220" x2="550" y2="220" stroke={colors.slateLight} strokeWidth="1" strokeDasharray="4" />
    <line x1="55" y1="160" x2="550" y2="160" stroke={colors.slateLight} strokeWidth="1" strokeDasharray="4" />
    <line x1="55" y1="100" x2="550" y2="100" stroke={colors.slateLight} strokeWidth="1" strokeDasharray="4" />
    
    {/* Without Attorney Bar */}
    <rect x="120" y="230" width="100" height="50" fill={colors.slateLight} rx="4" />
    <text x="170" y="260" textAnchor="middle" fill={colors.white} fontSize="14" fontWeight="bold">$21K</text>
    <text x="170" y="305" textAnchor="middle" fill={colors.slate} fontSize="13">Without</text>
    <text x="170" y="320" textAnchor="middle" fill={colors.slate} fontSize="13">Attorney</text>
    
    {/* With Attorney Bar */}
    <rect x="380" y="105" width="100" height="175" fill={colors.primary} rx="4" />
    <text x="430" y="200" textAnchor="middle" fill={colors.white} fontSize="16" fontWeight="bold">$74K</text>
    <text x="430" y="305" textAnchor="middle" fill={colors.secondary} fontSize="13">With</text>
    <text x="430" y="320" textAnchor="middle" fill={colors.secondary} fontSize="13">Attorney</text>
    
    {/* 3.5x Callout */}
    <g transform="translate(300, 140)">
      <rect x="-45" y="-25" width="90" height="50" fill={colors.secondary} rx="25" />
      <text x="0" y="5" textAnchor="middle" fill={colors.white} fontSize="18" fontWeight="bold">3.5x</text>
      <text x="0" y="20" textAnchor="middle" fill={colors.primary} fontSize="10">MORE</text>
    </g>
    
    {/* Arrow */}
    <path d="M220 200 L255 140" stroke={colors.secondary} strokeWidth="2" fill="none" markerEnd="url(#arrowhead)" />
    <defs>
      <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
        <polygon points="0 0, 10 3.5, 0 7" fill={colors.secondary} />
      </marker>
    </defs>
    
    {/* Source note */}
    <text x="300" y="345" textAnchor="middle" fill={colors.slateLight} fontSize="10">
      *Based on Insurance Research Council studies
    </text>
  </svg>
);

// ============================================
// 2. STATUTE OF LIMITATIONS TIMELINE
// Shows Washington's 3-year deadline
// ============================================
export const StatuteOfLimitationsTimeline = ({ className = '' }) => (
  <svg 
    viewBox="0 0 650 280" 
    className={`w-full max-w-2xl mx-auto ${className}`}
    role="img"
    aria-label="Timeline showing Washington's 3-year statute of limitations for personal injury claims"
  >
    {/* Background */}
    <rect width="650" height="280" fill={colors.rose50} rx="16" />
    
    {/* Title */}
    <text x="325" y="30" textAnchor="middle" fill={colors.secondary} fontSize="16" fontWeight="bold">
      Washington Personal Injury Statute of Limitations
    </text>
    
    {/* Main timeline line */}
    <line x1="50" y1="130" x2="600" y2="130" stroke={colors.secondary} strokeWidth="4" />
    
    {/* Year markers */}
    {[0, 1, 2, 3].map((year) => (
      <g key={year} transform={`translate(${100 + year * 166}, 130)`}>
        <circle cx="0" cy="0" r={year === 3 ? 20 : 12} fill={year === 3 ? colors.primary : colors.secondary} />
        <text x="0" y="5" textAnchor="middle" fill={colors.white} fontSize="12" fontWeight="bold">
          {year}
        </text>
        <text x="0" y="55" textAnchor="middle" fill={colors.secondary} fontSize="14" fontWeight="bold">
          Year {year}
        </text>
      </g>
    ))}
    
    {/* Injury marker */}
    <g transform="translate(100, 70)">
      <rect x="-35" y="-15" width="70" height="30" fill={colors.secondary} rx="4" />
      <text x="0" y="5" textAnchor="middle" fill={colors.white} fontSize="11" fontWeight="bold">
        INJURY
      </text>
    </g>
    
    {/* Safe zone */}
    <rect x="100" y="145" width="400" height="25" fill={colors.ocean} opacity="0.2" rx="4" />
    <text x="300" y="163" textAnchor="middle" fill={colors.ocean} fontSize="12" fontWeight="bold">
      SAFE TO FILE
    </text>
    
    {/* Deadline marker */}
    <g transform="translate(598, 70)">
      <rect x="-55" y="-20" width="110" height="40" fill={colors.primary} rx="6" />
      <text x="0" y="0" textAnchor="middle" fill={colors.white} fontSize="12" fontWeight="bold">
        DEADLINE
      </text>
      <text x="0" y="14" textAnchor="middle" fill={colors.white} fontSize="10">
        File by this date!
      </text>
    </g>
    
    {/* Warning zone */}
    <rect x="500" y="145" width="100" height="25" fill={colors.primary} opacity="0.3" rx="4" />
    <text x="550" y="163" textAnchor="middle" fill={colors.primaryDark} fontSize="10" fontWeight="bold">
      ACT NOW
    </text>
    
    {/* Exception callout */}
    <g transform="translate(325, 230)">
      <rect x="-160" y="-20" width="320" height="45" fill={colors.white} stroke={colors.primary} strokeWidth="2" rx="8" />
      <text x="0" y="0" textAnchor="middle" fill={colors.secondary} fontSize="12" fontWeight="bold">
        ⚠️ Exceptions: Minors, government claims, discovery rule
      </text>
      <text x="0" y="16" textAnchor="middle" fill={colors.slate} fontSize="11">
        Some cases have shorter or longer deadlines
      </text>
    </g>
  </svg>
);

// ============================================
// 2B. WHY STATUTES OF LIMITATIONS EXIST - ICON GRID
// Key reasons for the 3-year deadline
// ============================================
export const WhyStatutesExistGrid = ({ className = '' }) => (
  <div className={`my-8 ${className}`}>
    <div className="bg-gradient-to-br from-coral-50 to-white rounded-2xl border-2 border-coral-200 p-6 md:p-8">
      <h4 className="text-center font-heading font-bold text-secondary text-xl mb-8">
        Why Washington Has a 3-Year Deadline
      </h4>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { 
            icon: '🔍', 
            title: 'Preserve Evidence', 
            desc: 'Photos, records & memories fade over time' 
          },
          { 
            icon: '🛡️', 
            title: 'Protect Defendants', 
            desc: 'People deserve closure from old claims' 
          },
          { 
            icon: '⚖️', 
            title: 'Court Efficiency', 
            desc: 'Courts focus on cases that can be decided fairly' 
          },
          { 
            icon: '✅', 
            title: 'Encourage Action', 
            desc: 'Prompts timely investigation of claims' 
          },
        ].map((item, idx) => (
          <div key={idx} className="bg-white rounded-xl p-5 text-center shadow-sm hover:shadow-md transition-shadow">
            <div className="text-4xl mb-3">{item.icon}</div>
            <p className="font-bold text-secondary mb-2">{item.title}</p>
            <p className="text-sm text-slate-600">{item.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// ============================================
// 2C. EXCEPTIONS FLOWCHART
// Shows the two main exceptions to the 3-year rule
// ============================================
export const ExceptionsFlowchart = ({ className = '' }) => (
  <div className={`my-8 ${className}`}>
    <div className="bg-white rounded-2xl border-2 border-coral-200 p-6 md:p-8">
      <h4 className="text-center font-heading font-bold text-secondary text-xl mb-6">
        Exceptions That May Extend the Deadline
      </h4>
      <div className="grid md:grid-cols-2 gap-4 max-w-2xl mx-auto">
        {/* Discovery Rule */}
        <div className="bg-gradient-to-br from-primary/10 to-coral-100 border border-coral-200 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-2xl">🔎</span>
            <p className="font-bold text-secondary text-lg">Discovery Rule</p>
          </div>
          <p className="text-sm text-slate-700 mb-3">
            Clock starts when you knew (or should have known) about the injury and its cause
          </p>
          <p className="text-xs text-primary font-medium italic">
            Example: Hidden medical injury from toxic exposure
          </p>
        </div>
        
        {/* Tolling for Minors */}
        <div className="bg-gradient-to-br from-ocean-500/10 to-teal-100 border border-teal-200 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-2xl">👶</span>
            <p className="font-bold text-secondary text-lg">Tolling for Minors</p>
          </div>
          <p className="text-sm text-slate-700 mb-3">
            Clock is paused until the injured person turns 18 years old
          </p>
          <p className="text-xs text-ocean-600 font-medium italic">
            RCW 4.16.190 — Also applies to incapacitated persons
          </p>
        </div>
      </div>
      <p className="text-center text-sm text-slate-500 mt-5 flex items-center justify-center gap-2">
        <span className="text-amber-500">⚠️</span>
        Exceptions are limited—don't assume they apply to your case
      </p>
    </div>
  </div>
);

// ============================================
// 3. CAR ACCIDENT STEPS FLOWCHART
// What to do after a car accident
// ============================================
export const CarAccidentStepsFlowchart = ({ className = '' }) => (
  <svg 
    viewBox="0 0 700 520" 
    className={`w-full max-w-3xl mx-auto ${className}`}
    role="img"
    aria-label="Flowchart showing steps to take after a car accident in Washington"
  >
    {/* Background */}
    <rect width="700" height="520" fill={colors.rose50} rx="16" />
    
    {/* Title */}
    <text x="350" y="35" textAnchor="middle" fill={colors.secondary} fontSize="18" fontWeight="bold">
      What To Do After a Car Accident
    </text>
    
    {/* Step 1 */}
    <g transform="translate(350, 85)">
      <circle cx="0" cy="0" r="30" fill={colors.primary} />
      <text x="0" y="7" textAnchor="middle" fill={colors.white} fontSize="20" fontWeight="bold">1</text>
      <rect x="50" y="-20" width="200" height="45" fill={colors.white} rx="8" stroke={colors.primary} strokeWidth="2" />
      <text x="150" y="0" textAnchor="middle" fill={colors.secondary} fontSize="13" fontWeight="bold">CHECK FOR INJURIES</text>
      <text x="150" y="15" textAnchor="middle" fill={colors.slate} fontSize="11">Call 911 if needed</text>
    </g>
    
    {/* Arrow 1-2 */}
    <line x1="350" y1="115" x2="350" y2="145" stroke={colors.secondary} strokeWidth="2" markerEnd="url(#arrowhead2)" />
    
    {/* Step 2 */}
    <g transform="translate(350, 175)">
      <circle cx="0" cy="0" r="30" fill={colors.primary} />
      <text x="0" y="7" textAnchor="middle" fill={colors.white} fontSize="20" fontWeight="bold">2</text>
      <rect x="50" y="-20" width="200" height="45" fill={colors.white} rx="8" stroke={colors.primary} strokeWidth="2" />
      <text x="150" y="0" textAnchor="middle" fill={colors.secondary} fontSize="13" fontWeight="bold">MOVE TO SAFETY</text>
      <text x="150" y="15" textAnchor="middle" fill={colors.slate} fontSize="11">If possible, clear the road</text>
    </g>
    
    {/* Arrow 2-3 */}
    <line x1="350" y1="205" x2="350" y2="235" stroke={colors.secondary} strokeWidth="2" markerEnd="url(#arrowhead2)" />
    
    {/* Step 3 */}
    <g transform="translate(350, 265)">
      <circle cx="0" cy="0" r="30" fill={colors.primary} />
      <text x="0" y="7" textAnchor="middle" fill={colors.white} fontSize="20" fontWeight="bold">3</text>
      <rect x="50" y="-20" width="200" height="45" fill={colors.white} rx="8" stroke={colors.primary} strokeWidth="2" />
      <text x="150" y="0" textAnchor="middle" fill={colors.secondary} fontSize="13" fontWeight="bold">DOCUMENT EVERYTHING</text>
      <text x="150" y="15" textAnchor="middle" fill={colors.slate} fontSize="11">Photos, witnesses, details</text>
    </g>
    
    {/* Arrow 3-4 */}
    <line x1="350" y1="295" x2="350" y2="325" stroke={colors.secondary} strokeWidth="2" markerEnd="url(#arrowhead2)" />
    
    {/* Step 4 */}
    <g transform="translate(350, 355)">
      <circle cx="0" cy="0" r="30" fill={colors.primary} />
      <text x="0" y="7" textAnchor="middle" fill={colors.white} fontSize="20" fontWeight="bold">4</text>
      <rect x="50" y="-20" width="200" height="45" fill={colors.white} rx="8" stroke={colors.primary} strokeWidth="2" />
      <text x="150" y="0" textAnchor="middle" fill={colors.secondary} fontSize="13" fontWeight="bold">GET MEDICAL CARE</text>
      <text x="150" y="15" textAnchor="middle" fill={colors.slate} fontSize="11">Even if you feel fine</text>
    </g>
    
    {/* Arrow 4-5 */}
    <line x1="350" y1="385" x2="350" y2="415" stroke={colors.secondary} strokeWidth="2" markerEnd="url(#arrowhead2)" />
    
    {/* Step 5 - Highlighted */}
    <g transform="translate(350, 455)">
      <circle cx="0" cy="0" r="35" fill={colors.secondary} />
      <text x="0" y="7" textAnchor="middle" fill={colors.white} fontSize="20" fontWeight="bold">5</text>
      <rect x="55" y="-25" width="220" height="55" fill={colors.secondary} rx="8" />
      <text x="165" y="-5" textAnchor="middle" fill={colors.white} fontSize="13" fontWeight="bold">CONTACT AN ATTORNEY</text>
      <text x="165" y="12" textAnchor="middle" fill={colors.primary} fontSize="12">Before talking to insurance</text>
    </g>
    
    {/* Left side tips */}
    <g transform="translate(80, 175)">
      <rect x="-60" y="-65" width="130" height="130" fill={colors.white} rx="8" stroke={colors.slateLight} strokeWidth="1" />
      <text x="5" y="-45" textAnchor="middle" fill={colors.secondary} fontSize="11" fontWeight="bold">📸 TAKE PHOTOS OF:</text>
      <text x="5" y="-25" textAnchor="middle" fill={colors.slate} fontSize="10">• Vehicle damage</text>
      <text x="5" y="-10" textAnchor="middle" fill={colors.slate} fontSize="10">• License plates</text>
      <text x="5" y="5" textAnchor="middle" fill={colors.slate} fontSize="10">• Road conditions</text>
      <text x="5" y="20" textAnchor="middle" fill={colors.slate} fontSize="10">• Traffic signs</text>
      <text x="5" y="35" textAnchor="middle" fill={colors.slate} fontSize="10">• Your injuries</text>
      <text x="5" y="50" textAnchor="middle" fill={colors.slate} fontSize="10">• Skid marks</text>
    </g>
    
    {/* Arrow definition */}
    <defs>
      <marker id="arrowhead2" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
        <polygon points="0 0, 10 3.5, 0 7" fill={colors.secondary} />
      </marker>
    </defs>
  </svg>
);

// ============================================
// 4. MAINTENANCE & CURE INFOGRAPHIC
// For maritime workers
// ============================================
export const MaintenanceAndCureInfoGraphic = ({ className = '' }) => (
  <svg 
    viewBox="0 0 700 400" 
    className={`w-full max-w-3xl mx-auto ${className}`}
    role="img"
    aria-label="Infographic explaining Maintenance and Cure benefits for maritime workers"
  >
    {/* Background */}
    <rect width="700" height="400" fill="#f0fdfa" rx="16" />
    
    {/* Title */}
    <text x="350" y="35" textAnchor="middle" fill={colors.secondary} fontSize="18" fontWeight="bold">
      Maintenance & Cure: Your Maritime Rights
    </text>
    
    {/* Maintenance Section */}
    <g transform="translate(175, 150)">
      <rect x="-140" y="-80" width="280" height="200" fill={colors.white} rx="12" stroke={colors.ocean} strokeWidth="3" />
      <rect x="-140" y="-80" width="280" height="45" fill={colors.ocean} rx="12" />
      <rect x="-140" y="-35" width="280" height="5" fill={colors.ocean} />
      
      <text x="0" y="-50" textAnchor="middle" fill={colors.white} fontSize="16" fontWeight="bold">
        🏠 MAINTENANCE
      </text>
      
      <text x="0" y="-10" textAnchor="middle" fill={colors.secondary} fontSize="13" fontWeight="bold">
        Daily Living Expenses
      </text>
      
      <text x="-110" y="20" fill={colors.slate} fontSize="12">• Housing/rent costs</text>
      <text x="-110" y="40" fill={colors.slate} fontSize="12">• Food expenses</text>
      <text x="-110" y="60" fill={colors.slate} fontSize="12">• Utilities</text>
      <text x="-110" y="80" fill={colors.slate} fontSize="12">• Transportation</text>
      
      <rect x="-100" y="95" width="200" height="25" fill={colors.oceanLight} opacity="0.3" rx="4" />
      <text x="0" y="112" textAnchor="middle" fill={colors.ocean} fontSize="12" fontWeight="bold">
        ~$30-70/day typical rate
      </text>
    </g>
    
    {/* Plus sign */}
    <g transform="translate(350, 150)">
      <circle cx="0" cy="0" r="25" fill={colors.secondary} />
      <text x="0" y="8" textAnchor="middle" fill={colors.white} fontSize="24" fontWeight="bold">+</text>
    </g>
    
    {/* Cure Section */}
    <g transform="translate(525, 150)">
      <rect x="-140" y="-80" width="280" height="200" fill={colors.white} rx="12" stroke={colors.ocean} strokeWidth="3" />
      <rect x="-140" y="-80" width="280" height="45" fill={colors.ocean} rx="12" />
      <rect x="-140" y="-35" width="280" height="5" fill={colors.ocean} />
      
      <text x="0" y="-50" textAnchor="middle" fill={colors.white} fontSize="16" fontWeight="bold">
        🏥 CURE
      </text>
      
      <text x="0" y="-10" textAnchor="middle" fill={colors.secondary} fontSize="13" fontWeight="bold">
        Medical Treatment
      </text>
      
      <text x="-110" y="20" fill={colors.slate} fontSize="12">• Doctor visits</text>
      <text x="-110" y="40" fill={colors.slate} fontSize="12">• Hospital bills</text>
      <text x="-110" y="60" fill={colors.slate} fontSize="12">• Surgery costs</text>
      <text x="-110" y="80" fill={colors.slate} fontSize="12">• Rehabilitation</text>
      
      <rect x="-100" y="95" width="200" height="25" fill={colors.oceanLight} opacity="0.3" rx="4" />
      <text x="0" y="112" textAnchor="middle" fill={colors.ocean} fontSize="12" fontWeight="bold">
        100% covered until MMI
      </text>
    </g>
    
    {/* Bottom callout */}
    <g transform="translate(350, 350)">
      <rect x="-250" y="-25" width="500" height="50" fill={colors.secondary} rx="8" />
      <text x="0" y="0" textAnchor="middle" fill={colors.white} fontSize="13" fontWeight="bold">
        ⚖️ These benefits are OWED to you regardless of fault
      </text>
      <text x="0" y="18" textAnchor="middle" fill={colors.oceanLight} fontSize="11">
        MMI = Maximum Medical Improvement (when healing plateaus)
      </text>
    </g>
  </svg>
);

// ============================================
// 5. JONES ACT RIGHTS DIAGRAM
// Who qualifies and what protections exist
// ============================================
export const JonesActRightsDiagram = ({ className = '' }) => (
  <svg 
    viewBox="0 0 700 480" 
    className={`w-full max-w-3xl mx-auto ${className}`}
    role="img"
    aria-label="Diagram showing Jones Act protections for maritime workers"
  >
    {/* Background */}
    <rect width="700" height="480" fill="#f0fdfa" rx="16" />
    
    {/* Title */}
    <text x="350" y="35" textAnchor="middle" fill={colors.secondary} fontSize="18" fontWeight="bold">
      Jones Act Protections for Maritime Workers
    </text>
    
    {/* Center circle - "You" */}
    <g transform="translate(350, 210)">
      <circle cx="0" cy="0" r="60" fill={colors.ocean} />
      <text x="0" y="-10" textAnchor="middle" fill={colors.white} fontSize="14" fontWeight="bold">INJURED</text>
      <text x="0" y="10" textAnchor="middle" fill={colors.white} fontSize="14" fontWeight="bold">SEAMAN</text>
      <text x="0" y="30" textAnchor="middle" fill={colors.oceanLight} fontSize="11">(You)</text>
    </g>
    
    {/* Protection 1 - Top: NEGLIGENCE */}
    <g transform="translate(350, 85)">
      <rect x="-85" y="-35" width="170" height="70" fill={colors.white} rx="8" stroke={colors.ocean} strokeWidth="2" />
      <text x="0" y="-12" textAnchor="middle" fill={colors.secondary} fontSize="12" fontWeight="bold">NEGLIGENCE</text>
      <text x="0" y="5" textAnchor="middle" fill={colors.slate} fontSize="10">Sue employer for unsafe</text>
      <text x="0" y="20" textAnchor="middle" fill={colors.slate} fontSize="10">conditions or equipment</text>
      <line x1="0" y1="35" x2="0" y2="65" stroke={colors.ocean} strokeWidth="2" />
    </g>
    
    {/* Protection 2 - Right: MAINTENANCE */}
    <g transform="translate(530, 210)">
      <rect x="-85" y="-35" width="170" height="70" fill={colors.white} rx="8" stroke={colors.ocean} strokeWidth="2" />
      <text x="0" y="-12" textAnchor="middle" fill={colors.secondary} fontSize="12" fontWeight="bold">MAINTENANCE</text>
      <text x="0" y="5" textAnchor="middle" fill={colors.slate} fontSize="10">Daily living expenses</text>
      <text x="0" y="20" textAnchor="middle" fill={colors.slate} fontSize="10">until you recover</text>
      <line x1="-85" y1="0" x2="-115" y2="0" stroke={colors.ocean} strokeWidth="2" />
    </g>
    
    {/* Protection 3 - Bottom: CURE */}
    <g transform="translate(350, 335)">
      <rect x="-85" y="-35" width="170" height="70" fill={colors.white} rx="8" stroke={colors.ocean} strokeWidth="2" />
      <text x="0" y="-12" textAnchor="middle" fill={colors.secondary} fontSize="12" fontWeight="bold">CURE</text>
      <text x="0" y="5" textAnchor="middle" fill={colors.slate} fontSize="10">Full medical treatment</text>
      <text x="0" y="20" textAnchor="middle" fill={colors.slate} fontSize="10">paid by employer</text>
      <line x1="0" y1="-35" x2="0" y2="-65" stroke={colors.ocean} strokeWidth="2" />
    </g>
    
    {/* Protection 4 - Left: UNSEAWORTHINESS */}
    <g transform="translate(170, 210)">
      <rect x="-85" y="-35" width="170" height="70" fill={colors.white} rx="8" stroke={colors.ocean} strokeWidth="2" />
      <text x="0" y="-12" textAnchor="middle" fill={colors.secondary} fontSize="12" fontWeight="bold">UNSEAWORTHINESS</text>
      <text x="0" y="5" textAnchor="middle" fill={colors.slate} fontSize="10">Claim if vessel was</text>
      <text x="0" y="20" textAnchor="middle" fill={colors.slate} fontSize="10">unsafe or defective</text>
      <line x1="85" y1="0" x2="115" y2="0" stroke={colors.ocean} strokeWidth="2" />
    </g>
    
    {/* Who qualifies box */}
    <g transform="translate(350, 435)">
      <rect x="-280" y="-35" width="560" height="60" fill={colors.white} rx="8" stroke={colors.secondary} strokeWidth="2" />
      <text x="0" y="-8" textAnchor="middle" fill={colors.secondary} fontSize="12" fontWeight="bold">
        WHO QUALIFIES? Workers who spend 30%+ of work time on vessels in navigation
      </text>
      <text x="0" y="12" textAnchor="middle" fill={colors.slate} fontSize="11">
        Includes: Deckhands, engineers, cooks, fishermen, tugboat crews, ferryboat workers
      </text>
    </g>
  </svg>
);

// ============================================
// 6. FIRST RESPONDER CLAIMS DECISION TREE
// Do you have a civil claim beyond workers' comp?
// ============================================
export const FirstResponderDecisionTree = ({ className = '' }) => (
  <svg 
    viewBox="0 0 700 500" 
    className={`w-full max-w-3xl mx-auto ${className}`}
    role="img"
    aria-label="Decision tree for first responders to determine if they have a civil claim"
  >
    {/* Background */}
    <rect width="700" height="500" fill={colors.rose50} rx="16" />
    
    {/* Title */}
    <text x="350" y="30" textAnchor="middle" fill={colors.secondary} fontSize="16" fontWeight="bold">
      First Responder Injury: Do You Have a Civil Claim?
    </text>
    
    {/* Start */}
    <g transform="translate(350, 75)">
      <rect x="-100" y="-25" width="200" height="50" fill={colors.secondary} rx="25" />
      <text x="0" y="5" textAnchor="middle" fill={colors.white} fontSize="14" fontWeight="bold">
        Were you injured on duty?
      </text>
    </g>
    
    {/* Q1 branches */}
    <line x1="350" y1="100" x2="350" y2="140" stroke={colors.secondary} strokeWidth="2" />
    
    {/* Q2 */}
    <g transform="translate(350, 170)">
      <rect x="-120" y="-25" width="240" height="50" fill={colors.white} rx="8" stroke={colors.primary} strokeWidth="2" />
      <text x="0" y="0" textAnchor="middle" fill={colors.secondary} fontSize="12" fontWeight="bold">
        Was a third party (not your employer)
      </text>
      <text x="0" y="15" textAnchor="middle" fill={colors.secondary} fontSize="12" fontWeight="bold">
        at fault?
      </text>
    </g>
    
    {/* YES path */}
    <line x1="230" y1="170" x2="150" y2="170" stroke={colors.ocean} strokeWidth="2" />
    <line x1="150" y1="170" x2="150" y2="250" stroke={colors.ocean} strokeWidth="2" />
    <text x="180" y="165" fill={colors.ocean} fontSize="11" fontWeight="bold">YES</text>
    
    <g transform="translate(150, 290)">
      <rect x="-110" y="-35" width="220" height="70" fill={colors.ocean} rx="8" />
      <text x="0" y="-10" textAnchor="middle" fill={colors.white} fontSize="13" fontWeight="bold">
        ✓ CIVIL CLAIM LIKELY
      </text>
      <text x="0" y="10" textAnchor="middle" fill={colors.white} fontSize="11">
        Sue the at-fault party
      </text>
      <text x="0" y="25" textAnchor="middle" fill={colors.oceanLight} fontSize="10">
        (drunk driver, property owner, etc.)
      </text>
    </g>
    
    {/* NO path */}
    <line x1="470" y1="170" x2="550" y2="170" stroke={colors.primary} strokeWidth="2" />
    <line x1="550" y1="170" x2="550" y2="250" stroke={colors.primary} strokeWidth="2" />
    <text x="500" y="165" fill={colors.primary} fontSize="11" fontWeight="bold">NO</text>
    
    {/* Q3 */}
    <g transform="translate(550, 290)">
      <rect x="-110" y="-35" width="220" height="70" fill={colors.white} rx="8" stroke={colors.primary} strokeWidth="2" />
      <text x="0" y="-10" textAnchor="middle" fill={colors.secondary} fontSize="12" fontWeight="bold">
        Was there defective equipment
      </text>
      <text x="0" y="5" textAnchor="middle" fill={colors.secondary} fontSize="12" fontWeight="bold">
        or product failure?
      </text>
    </g>
    
    {/* Q3 YES */}
    <line x1="440" y1="290" x2="350" y2="290" stroke={colors.ocean} strokeWidth="2" />
    <line x1="350" y1="290" x2="350" y2="370" stroke={colors.ocean} strokeWidth="2" />
    <text x="400" y="285" fill={colors.ocean} fontSize="11" fontWeight="bold">YES</text>
    
    <g transform="translate(350, 410)">
      <rect x="-110" y="-35" width="220" height="70" fill={colors.ocean} rx="8" />
      <text x="0" y="-10" textAnchor="middle" fill={colors.white} fontSize="13" fontWeight="bold">
        ✓ PRODUCT LIABILITY
      </text>
      <text x="0" y="10" textAnchor="middle" fill={colors.white} fontSize="11">
        Sue the manufacturer
      </text>
    </g>
    
    {/* Q3 NO */}
    <line x1="550" y1="325" x2="550" y2="400" stroke={colors.slateLight} strokeWidth="2" />
    <text x="565" y="350" fill={colors.slateLight} fontSize="11" fontWeight="bold">NO</text>
    
    <g transform="translate(550, 440)">
      <rect x="-90" y="-30" width="180" height="55" fill={colors.slateLight} rx="8" />
      <text x="0" y="-5" textAnchor="middle" fill={colors.white} fontSize="12" fontWeight="bold">
        Workers' Comp Only
      </text>
      <text x="0" y="12" textAnchor="middle" fill={colors.white} fontSize="10">
        (But consult attorney to be sure)
      </text>
    </g>
  </svg>
);

// ============================================
// 7. DOCUMENTS CHECKLIST GRAPHIC
// What to gather after an accident
// ============================================
export const DocumentsChecklistGraphic = ({ className = '' }) => (
  <svg 
    viewBox="0 0 650 420" 
    className={`w-full max-w-2xl mx-auto ${className}`}
    role="img"
    aria-label="Checklist of documents to gather after a personal injury"
  >
    {/* Background */}
    <rect width="650" height="420" fill={colors.rose50} rx="16" />
    
    {/* Title */}
    <text x="325" y="35" textAnchor="middle" fill={colors.secondary} fontSize="18" fontWeight="bold">
      📋 Documents to Gather for Your Claim
    </text>
    
    {/* Column 1 - Immediate */}
    <g transform="translate(115, 80)">
      <rect x="-95" y="-20" width="190" height="35" fill={colors.primary} rx="6" />
      <text x="0" y="5" textAnchor="middle" fill={colors.white} fontSize="13" fontWeight="bold">
        IMMEDIATELY AFTER
      </text>
      
      {['Police report copy', 'Photos of scene', 'Witness contact info', 'Insurance info exchanged', 'Your written notes'].map((item, i) => (
        <g key={i} transform={`translate(0, ${45 + i * 35})`}>
          <rect x="-95" y="-12" width="190" height="28" fill={colors.white} rx="4" />
          <text x="-80" y="5" fill={colors.secondary} fontSize="12">☐ {item}</text>
        </g>
      ))}
    </g>
    
    {/* Column 2 - Medical */}
    <g transform="translate(325, 80)">
      <rect x="-95" y="-20" width="190" height="35" fill={colors.ocean} rx="6" />
      <text x="0" y="5" textAnchor="middle" fill={colors.white} fontSize="13" fontWeight="bold">
        MEDICAL RECORDS
      </text>
      
      {['ER/hospital records', 'Doctor visit notes', 'Test results (X-rays, MRI)', 'Prescription receipts', 'Therapy records'].map((item, i) => (
        <g key={i} transform={`translate(0, ${45 + i * 35})`}>
          <rect x="-95" y="-12" width="190" height="28" fill={colors.white} rx="4" />
          <text x="-80" y="5" fill={colors.secondary} fontSize="12">☐ {item}</text>
        </g>
      ))}
    </g>
    
    {/* Column 3 - Financial */}
    <g transform="translate(535, 80)">
      <rect x="-95" y="-20" width="190" height="35" fill={colors.secondary} rx="6" />
      <text x="0" y="5" textAnchor="middle" fill={colors.white} fontSize="13" fontWeight="bold">
        FINANCIAL PROOF
      </text>
      
      {['Medical bills', 'Pay stubs (lost wages)', 'Repair estimates', 'Rental car receipts', 'Out-of-pocket expenses'].map((item, i) => (
        <g key={i} transform={`translate(0, ${45 + i * 35})`}>
          <rect x="-95" y="-12" width="190" height="28" fill={colors.white} rx="4" />
          <text x="-80" y="5" fill={colors.secondary} fontSize="12">☐ {item}</text>
        </g>
      ))}
    </g>
    
    {/* Pro tip */}
    <g transform="translate(325, 385)">
      <rect x="-280" y="-25" width="560" height="50" fill={colors.white} rx="8" stroke={colors.primary} strokeWidth="2" />
      <text x="0" y="0" textAnchor="middle" fill={colors.secondary} fontSize="12" fontWeight="bold">
        💡 PRO TIP: Create a dedicated folder (physical + digital) for all documents
      </text>
      <text x="0" y="18" textAnchor="middle" fill={colors.slate} fontSize="11">
        Keep originals safe and make copies for your attorney
      </text>
    </g>
  </svg>
);

// ============================================
// 8. CASE VALUE FACTORS INFOGRAPHIC
// What determines compensation amount
// ============================================
export const CaseValueFactorsGraphic = ({ className = '' }) => (
  <svg 
    viewBox="0 0 700 380" 
    className={`w-full max-w-3xl mx-auto ${className}`}
    role="img"
    aria-label="Infographic showing factors that determine personal injury case value"
  >
    {/* Background */}
    <rect width="700" height="380" fill={colors.rose50} rx="16" />
    
    {/* Title */}
    <text x="350" y="35" textAnchor="middle" fill={colors.secondary} fontSize="18" fontWeight="bold">
      What Determines Your Case Value?
    </text>
    
    {/* Center "CASE VALUE" */}
    <g transform="translate(350, 190)">
      <circle cx="0" cy="0" r="55" fill={colors.secondary} />
      <text x="0" y="-5" textAnchor="middle" fill={colors.white} fontSize="14" fontWeight="bold">CASE</text>
      <text x="0" y="15" textAnchor="middle" fill={colors.white} fontSize="14" fontWeight="bold">VALUE</text>
    </g>
    
    {/* Factor 1 - Medical Bills (Top) */}
    <g transform="translate(350, 80)">
      <rect x="-70" y="-25" width="140" height="50" fill={colors.primary} rx="8" />
      <text x="0" y="5" textAnchor="middle" fill={colors.white} fontSize="12" fontWeight="bold">💊 Medical Bills</text>
      <line x1="0" y1="25" x2="0" y2="75" stroke={colors.secondary} strokeWidth="2" />
    </g>
    
    {/* Factor 2 - Lost Income (Top Right) */}
    <g transform="translate(500, 120)">
      <rect x="-70" y="-25" width="140" height="50" fill={colors.primary} rx="8" />
      <text x="0" y="5" textAnchor="middle" fill={colors.white} fontSize="12" fontWeight="bold">💼 Lost Income</text>
      <line x1="-70" y1="0" x2="-95" y2="40" stroke={colors.secondary} strokeWidth="2" />
    </g>
    
    {/* Factor 3 - Pain & Suffering (Right) */}
    <g transform="translate(520, 220)">
      <rect x="-70" y="-25" width="140" height="50" fill={colors.ocean} rx="8" />
      <text x="0" y="5" textAnchor="middle" fill={colors.white} fontSize="12" fontWeight="bold">😣 Pain & Suffering</text>
      <line x1="-70" y1="0" x2="-110" y2="0" stroke={colors.secondary} strokeWidth="2" />
    </g>
    
    {/* Factor 4 - Liability (Bottom Right) */}
    <g transform="translate(480, 290)">
      <rect x="-70" y="-25" width="140" height="50" fill={colors.ocean} rx="8" />
      <text x="0" y="5" textAnchor="middle" fill={colors.white} fontSize="12" fontWeight="bold">⚖️ Clear Liability</text>
      <line x1="-70" y1="-15" x2="-100" y2="-50" stroke={colors.secondary} strokeWidth="2" />
    </g>
    
    {/* Factor 5 - Future Costs (Bottom) */}
    <g transform="translate(350, 310)">
      <rect x="-70" y="-25" width="140" height="50" fill={colors.primary} rx="8" />
      <text x="0" y="5" textAnchor="middle" fill={colors.white} fontSize="12" fontWeight="bold">🔮 Future Costs</text>
      <line x1="0" y1="-25" x2="0" y2="-65" stroke={colors.secondary} strokeWidth="2" />
    </g>
    
    {/* Factor 6 - Evidence (Bottom Left) */}
    <g transform="translate(220, 290)">
      <rect x="-70" y="-25" width="140" height="50" fill={colors.ocean} rx="8" />
      <text x="0" y="5" textAnchor="middle" fill={colors.white} fontSize="12" fontWeight="bold">📸 Strong Evidence</text>
      <line x1="70" y1="-15" x2="100" y2="-50" stroke={colors.secondary} strokeWidth="2" />
    </g>
    
    {/* Factor 7 - Permanent Injury (Left) */}
    <g transform="translate(180, 220)">
      <rect x="-70" y="-25" width="140" height="50" fill={colors.primary} rx="8" />
      <text x="0" y="5" textAnchor="middle" fill={colors.white} fontSize="12" fontWeight="bold">🩹 Permanence</text>
      <line x1="70" y1="0" x2="110" y2="0" stroke={colors.secondary} strokeWidth="2" />
    </g>
    
    {/* Factor 8 - Insurance (Top Left) */}
    <g transform="translate(200, 120)">
      <rect x="-70" y="-25" width="140" height="50" fill={colors.slateLight} rx="8" />
      <text x="0" y="5" textAnchor="middle" fill={colors.white} fontSize="12" fontWeight="bold">🛡️ Insurance Limits</text>
      <line x1="70" y1="0" x2="95" y2="40" stroke={colors.secondary} strokeWidth="2" />
    </g>
    
    {/* Bottom note */}
    <text x="350" y="365" textAnchor="middle" fill={colors.slate} fontSize="11">
      Higher values on more factors = higher potential compensation
    </text>
  </svg>
);

// ============================================
// 9. CLAIMS PROCESS TIMELINE
// How long does a case take?
// ============================================
export const ClaimsProcessTimeline = ({ className = '' }) => (
  <svg 
    viewBox="0 0 750 220" 
    className={`w-full max-w-4xl mx-auto ${className}`}
    role="img"
    aria-label="Timeline showing typical personal injury claims process"
  >
    {/* Background */}
    <rect width="750" height="220" fill={colors.rose50} rx="16" />
    
    {/* Title */}
    <text x="375" y="28" textAnchor="middle" fill={colors.secondary} fontSize="16" fontWeight="bold">
      Typical Personal Injury Claims Timeline
    </text>
    
    {/* Timeline line */}
    <line x1="50" y1="110" x2="700" y2="110" stroke={colors.secondary} strokeWidth="4" />
    
    {/* Stage 1 */}
    <g transform="translate(80, 110)">
      <circle cx="0" cy="0" r="18" fill={colors.primary} />
      <text x="0" y="5" textAnchor="middle" fill={colors.white} fontSize="12" fontWeight="bold">1</text>
      <text x="0" y="-35" textAnchor="middle" fill={colors.secondary} fontSize="11" fontWeight="bold">Initial</text>
      <text x="0" y="-22" textAnchor="middle" fill={colors.secondary} fontSize="11" fontWeight="bold">Consult</text>
      <text x="0" y="40" textAnchor="middle" fill={colors.slate} fontSize="10">Week 1</text>
    </g>
    
    {/* Stage 2 */}
    <g transform="translate(200, 110)">
      <circle cx="0" cy="0" r="18" fill={colors.primary} />
      <text x="0" y="5" textAnchor="middle" fill={colors.white} fontSize="12" fontWeight="bold">2</text>
      <text x="0" y="-35" textAnchor="middle" fill={colors.secondary} fontSize="11" fontWeight="bold">Medical</text>
      <text x="0" y="-22" textAnchor="middle" fill={colors.secondary} fontSize="11" fontWeight="bold">Treatment</text>
      <text x="0" y="40" textAnchor="middle" fill={colors.slate} fontSize="10">Ongoing</text>
    </g>
    
    {/* Stage 3 */}
    <g transform="translate(320, 110)">
      <circle cx="0" cy="0" r="18" fill={colors.primary} />
      <text x="0" y="5" textAnchor="middle" fill={colors.white} fontSize="12" fontWeight="bold">3</text>
      <text x="0" y="-35" textAnchor="middle" fill={colors.secondary} fontSize="11" fontWeight="bold">Investigation</text>
      <text x="0" y="-22" textAnchor="middle" fill={colors.secondary} fontSize="11" fontWeight="bold">& Evidence</text>
      <text x="0" y="40" textAnchor="middle" fill={colors.slate} fontSize="10">1-3 mo</text>
    </g>
    
    {/* Stage 4 */}
    <g transform="translate(440, 110)">
      <circle cx="0" cy="0" r="18" fill={colors.ocean} />
      <text x="0" y="5" textAnchor="middle" fill={colors.white} fontSize="12" fontWeight="bold">4</text>
      <text x="0" y="-35" textAnchor="middle" fill={colors.secondary} fontSize="11" fontWeight="bold">Demand</text>
      <text x="0" y="-22" textAnchor="middle" fill={colors.secondary} fontSize="11" fontWeight="bold">Letter</text>
      <text x="0" y="40" textAnchor="middle" fill={colors.slate} fontSize="10">3-6 mo</text>
    </g>
    
    {/* Stage 5 */}
    <g transform="translate(560, 110)">
      <circle cx="0" cy="0" r="18" fill={colors.ocean} />
      <text x="0" y="5" textAnchor="middle" fill={colors.white} fontSize="12" fontWeight="bold">5</text>
      <text x="0" y="-35" textAnchor="middle" fill={colors.secondary} fontSize="11" fontWeight="bold">Negotiation/</text>
      <text x="0" y="-22" textAnchor="middle" fill={colors.secondary} fontSize="11" fontWeight="bold">Mediation</text>
      <text x="0" y="40" textAnchor="middle" fill={colors.slate} fontSize="10">6-12 mo</text>
    </g>
    
    {/* Stage 6 */}
    <g transform="translate(680, 110)">
      <circle cx="0" cy="0" r="22" fill={colors.secondary} />
      <text x="0" y="5" textAnchor="middle" fill={colors.white} fontSize="12" fontWeight="bold">6</text>
      <text x="0" y="-40" textAnchor="middle" fill={colors.secondary} fontSize="11" fontWeight="bold">Settlement</text>
      <text x="0" y="-27" textAnchor="middle" fill={colors.secondary} fontSize="11" fontWeight="bold">or Trial</text>
      <text x="0" y="45" textAnchor="middle" fill={colors.slate} fontSize="10">12-24 mo</text>
    </g>
    
    {/* Total time */}
    <g transform="translate(375, 185)">
      <rect x="-300" y="-18" width="600" height="36" fill={colors.white} rx="8" stroke={colors.primary} strokeWidth="2" />
      <text x="0" y="5" textAnchor="middle" fill={colors.secondary} fontSize="12" fontWeight="bold">
        📅 Most cases resolve in 6-18 months (varies by complexity)
      </text>
    </g>
  </svg>
);

// ============================================
// 10. INSURANCE VS ATTORNEY COMPARISON
// Why not just deal with insurance directly?
// ============================================
export const InsuranceVsAttorneyComparison = ({ className = '' }) => (
  <svg 
    viewBox="0 0 700 400" 
    className={`w-full max-w-3xl mx-auto ${className}`}
    role="img"
    aria-label="Comparison of handling a claim yourself vs with an attorney"
  >
    {/* Background */}
    <rect width="700" height="400" fill={colors.rose50} rx="16" />
    
    {/* Title */}
    <text x="350" y="35" textAnchor="middle" fill={colors.secondary} fontSize="18" fontWeight="bold">
      Handling Your Claim: DIY vs. Attorney
    </text>
    
    {/* VS Circle */}
    <g transform="translate(350, 200)">
      <circle cx="0" cy="0" r="30" fill={colors.secondary} />
      <text x="0" y="7" textAnchor="middle" fill={colors.white} fontSize="16" fontWeight="bold">VS</text>
    </g>
    
    {/* Left Side - DIY */}
    <g transform="translate(160, 80)">
      <rect x="-130" y="-25" width="260" height="280" fill={colors.white} rx="12" stroke={colors.slateLight} strokeWidth="2" />
      <rect x="-130" y="-25" width="260" height="45" fill={colors.slateLight} rx="12" />
      <rect x="-130" y="15" width="260" height="5" fill={colors.slateLight} />
      
      <text x="0" y="0" textAnchor="middle" fill={colors.white} fontSize="14" fontWeight="bold">
        ❌ WITHOUT ATTORNEY
      </text>
      
      <text x="-110" y="45" fill={colors.slate} fontSize="12">• Insurance controls timeline</text>
      <text x="-110" y="70" fill={colors.slate} fontSize="12">• May miss deadlines</text>
      <text x="-110" y="95" fill={colors.slate} fontSize="12">• Accept first offer (usually low)</text>
      <text x="-110" y="120" fill={colors.slate} fontSize="12">• No leverage in negotiations</text>
      <text x="-110" y="145" fill={colors.slate} fontSize="12">• Statements used against you</text>
      <text x="-110" y="170" fill={colors.slate} fontSize="12">• Handle paperwork yourself</text>
      
      <rect x="-110" y="190" width="220" height="40" fill={colors.rose100} rx="6" />
      <text x="0" y="215" textAnchor="middle" fill={colors.slate} fontSize="13" fontWeight="bold">
        Avg. Settlement: $21,000
      </text>
    </g>
    
    {/* Right Side - With Attorney */}
    <g transform="translate(540, 80)">
      <rect x="-130" y="-25" width="260" height="280" fill={colors.white} rx="12" stroke={colors.primary} strokeWidth="3" />
      <rect x="-130" y="-25" width="260" height="45" fill={colors.primary} rx="12" />
      <rect x="-130" y="15" width="260" height="5" fill={colors.primary} />
      
      <text x="0" y="0" textAnchor="middle" fill={colors.white} fontSize="14" fontWeight="bold">
        ✓ WITH ATTORNEY
      </text>
      
      <text x="-110" y="45" fill={colors.secondary} fontSize="12">• Professional case management</text>
      <text x="-110" y="70" fill={colors.secondary} fontSize="12">• Never miss a deadline</text>
      <text x="-110" y="95" fill={colors.secondary} fontSize="12">• Negotiate for full value</text>
      <text x="-110" y="120" fill={colors.secondary} fontSize="12">• Insurance takes you seriously</text>
      <text x="-110" y="145" fill={colors.secondary} fontSize="12">• Communications protected</text>
      <text x="-110" y="170" fill={colors.secondary} fontSize="12">• We handle ALL paperwork</text>
      
      <rect x="-110" y="190" width="220" height="40" fill={colors.rose100} rx="6" />
      <text x="0" y="215" textAnchor="middle" fill={colors.primary} fontSize="13" fontWeight="bold">
        Avg. Settlement: $74,000
      </text>
    </g>
    
    {/* Bottom note */}
    <text x="350" y="380" textAnchor="middle" fill={colors.slate} fontSize="11">
      No upfront cost • Contingency fee (typically 33%) • You still come out ahead
    </text>
  </svg>
);

// ============================================
// 11. INSURANCE ADJUSTER TACTICS WARNING
// What adjusters do and why to be cautious
// ============================================
export const InsuranceAdjusterTacticsGraphic = ({ className = '' }) => (
  <svg 
    viewBox="0 0 700 420" 
    className={`w-full max-w-3xl mx-auto ${className}`}
    role="img"
    aria-label="Warning diagram showing insurance adjuster tactics to watch for"
  >
    {/* Background */}
    <rect width="700" height="420" fill={colors.rose50} rx="16" />
    
    {/* Title */}
    <text x="350" y="35" textAnchor="middle" fill={colors.secondary} fontSize="18" fontWeight="bold">
      ⚠️ Why Insurance Adjusters Want Your Recorded Statement
    </text>
    
    {/* Center - Adjuster icon */}
    <g transform="translate(350, 140)">
      <circle cx="0" cy="0" r="50" fill={colors.slateLight} />
      <text x="0" y="-10" textAnchor="middle" fill={colors.white} fontSize="24">📞</text>
      <text x="0" y="15" textAnchor="middle" fill={colors.white} fontSize="11" fontWeight="bold">ADJUSTER</text>
      <text x="0" y="28" textAnchor="middle" fill={colors.white} fontSize="9">calls you</text>
    </g>
    
    {/* Tactic 1 - Left */}
    <g transform="translate(120, 140)">
      <rect x="-90" y="-45" width="180" height="90" fill={colors.white} rx="8" stroke="#ef4444" strokeWidth="2" />
      <text x="0" y="-25" textAnchor="middle" fill="#ef4444" fontSize="11" fontWeight="bold">❌ TACTIC #1</text>
      <text x="0" y="-8" textAnchor="middle" fill={colors.secondary} fontSize="11" fontWeight="bold">"Just a quick statement"</text>
      <text x="0" y="10" textAnchor="middle" fill={colors.slate} fontSize="10">Locks you into early details</text>
      <text x="0" y="25" textAnchor="middle" fill={colors.slate} fontSize="10">before injuries develop</text>
      <line x1="90" y1="0" x2="130" y2="0" stroke="#ef4444" strokeWidth="2" strokeDasharray="4" />
    </g>
    
    {/* Tactic 2 - Right */}
    <g transform="translate(580, 140)">
      <rect x="-90" y="-45" width="180" height="90" fill={colors.white} rx="8" stroke="#ef4444" strokeWidth="2" />
      <text x="0" y="-25" textAnchor="middle" fill="#ef4444" fontSize="11" fontWeight="bold">❌ TACTIC #2</text>
      <text x="0" y="-8" textAnchor="middle" fill={colors.secondary} fontSize="11" fontWeight="bold">"We need this to proceed"</text>
      <text x="0" y="10" textAnchor="middle" fill={colors.slate} fontSize="10">FALSE - you are NOT</text>
      <text x="0" y="25" textAnchor="middle" fill={colors.slate} fontSize="10">required to give one</text>
      <line x1="-90" y1="0" x2="-130" y2="0" stroke="#ef4444" strokeWidth="2" strokeDasharray="4" />
    </g>
    
    {/* Tactic 3 - Bottom Left */}
    <g transform="translate(180, 280)">
      <rect x="-90" y="-45" width="180" height="90" fill={colors.white} rx="8" stroke="#ef4444" strokeWidth="2" />
      <text x="0" y="-25" textAnchor="middle" fill="#ef4444" fontSize="11" fontWeight="bold">❌ TACTIC #3</text>
      <text x="0" y="-8" textAnchor="middle" fill={colors.secondary} fontSize="11" fontWeight="bold">"How are you feeling?"</text>
      <text x="0" y="10" textAnchor="middle" fill={colors.slate} fontSize="10">"I'm okay" used later to</text>
      <text x="0" y="25" textAnchor="middle" fill={colors.slate} fontSize="10">minimize your injuries</text>
      <line x1="0" y1="-45" x2="0" y2="-85" stroke="#ef4444" strokeWidth="2" strokeDasharray="4" />
    </g>
    
    {/* Tactic 4 - Bottom Right */}
    <g transform="translate(520, 280)">
      <rect x="-90" y="-45" width="180" height="90" fill={colors.white} rx="8" stroke="#ef4444" strokeWidth="2" />
      <text x="0" y="-25" textAnchor="middle" fill="#ef4444" fontSize="11" fontWeight="bold">❌ TACTIC #4</text>
      <text x="0" y="-8" textAnchor="middle" fill={colors.secondary} fontSize="11" fontWeight="bold">"Describe what happened"</text>
      <text x="0" y="10" textAnchor="middle" fill={colors.slate} fontSize="10">Looking for ANY detail to</text>
      <text x="0" y="25" textAnchor="middle" fill={colors.slate} fontSize="10">shift blame to you</text>
      <line x1="0" y1="-45" x2="0" y2="-85" stroke="#ef4444" strokeWidth="2" strokeDasharray="4" />
    </g>
    
    {/* Bottom Warning Box */}
    <g transform="translate(350, 385)">
      <rect x="-280" y="-25" width="560" height="50" fill={colors.secondary} rx="8" />
      <text x="0" y="-2" textAnchor="middle" fill={colors.white} fontSize="12" fontWeight="bold">
        🛡️ REMEMBER: Their goal is to PAY YOU LESS, not help you recover
      </text>
      <text x="0" y="15" textAnchor="middle" fill={colors.primary} fontSize="11">
        You control the process — politely decline until you've spoken with an attorney
      </text>
    </g>
  </svg>
);

// ============================================
// 12. AFTER ACCIDENT STEPS (INSURANCE)
// What to do instead of giving a statement
// ============================================
export const AfterAccidentInsuranceSteps = ({ className = '' }) => (
  <svg 
    viewBox="0 0 700 580" 
    className={`w-full max-w-3xl mx-auto ${className}`}
    role="img"
    aria-label="Step-by-step guide for handling insurance after an accident"
  >
    {/* Background */}
    <rect width="700" height="580" fill={colors.rose50} rx="16" />
    
    {/* Title */}
    <text x="350" y="35" textAnchor="middle" fill={colors.secondary} fontSize="18" fontWeight="bold">
      ✓ What To Do Instead: 7 Steps to Protect Your Claim
    </text>
    
    {/* Left Column - Steps 1-5 */}
    {/* Step 1 */}
    <g transform="translate(50, 80)">
      <circle cx="25" cy="15" r="22" fill={colors.primary} />
      <text x="25" y="21" textAnchor="middle" fill={colors.white} fontSize="14" fontWeight="bold">1</text>
      <text x="60" y="10" fill={colors.secondary} fontSize="12" fontWeight="bold">Prioritize Medical Care</text>
      <text x="60" y="28" fill={colors.slate} fontSize="11">Get checked immediately — document injuries from Day 1</text>
    </g>
    
    {/* Connector line */}
    <line x1="75" y1="120" x2="75" y2="140" stroke={colors.primary} strokeWidth="2" />
    
    {/* Step 2 */}
    <g transform="translate(50, 150)">
      <circle cx="25" cy="15" r="22" fill={colors.primary} />
      <text x="25" y="21" textAnchor="middle" fill={colors.white} fontSize="14" fontWeight="bold">2</text>
      <text x="60" y="10" fill={colors.secondary} fontSize="12" fontWeight="bold">Exchange Basic Info at Scene</text>
      <text x="60" y="28" fill={colors.slate} fontSize="11">Names, insurance, contact only — don't discuss fault</text>
    </g>
    
    {/* Connector line */}
    <line x1="75" y1="190" x2="75" y2="210" stroke={colors.primary} strokeWidth="2" />
    
    {/* Step 3 */}
    <g transform="translate(50, 220)">
      <circle cx="25" cy="15" r="22" fill={colors.primary} />
      <text x="25" y="21" textAnchor="middle" fill={colors.white} fontSize="14" fontWeight="bold">3</text>
      <text x="60" y="10" fill={colors.secondary} fontSize="12" fontWeight="bold">Notify YOUR Insurance (Briefly)</text>
      <text x="60" y="28" fill={colors.slate} fontSize="11">Report the crash — no recorded statement needed</text>
    </g>
    
    {/* Connector line */}
    <line x1="75" y1="260" x2="75" y2="280" stroke={colors.primary} strokeWidth="2" />
    
    {/* Step 4 - Highlighted */}
    <g transform="translate(50, 290)">
      <rect x="-10" y="-15" width="320" height="70" fill={colors.white} rx="8" stroke={colors.primary} strokeWidth="3" />
      <circle cx="25" cy="20" r="22" fill={colors.secondary} />
      <text x="25" y="26" textAnchor="middle" fill={colors.white} fontSize="14" fontWeight="bold">4</text>
      <text x="60" y="10" fill={colors.secondary} fontSize="12" fontWeight="bold">Politely Decline Their Request</text>
      <text x="60" y="30" fill={colors.primary} fontSize="10" fontStyle="italic">"I'm not ready to give a recorded statement.</text>
      <text x="60" y="44" fill={colors.primary} fontSize="10" fontStyle="italic">Please send communications in writing."</text>
    </g>
    
    {/* Connector line */}
    <line x1="75" y1="365" x2="75" y2="385" stroke={colors.primary} strokeWidth="2" />
    
    {/* Step 5 */}
    <g transform="translate(50, 395)">
      <circle cx="25" cy="15" r="22" fill={colors.ocean} />
      <text x="25" y="21" textAnchor="middle" fill={colors.white} fontSize="14" fontWeight="bold">5</text>
      <text x="60" y="10" fill={colors.secondary} fontSize="12" fontWeight="bold">Contact an Attorney Early</text>
      <text x="60" y="28" fill={colors.slate} fontSize="11">We communicate on your behalf — nothing misinterpreted</text>
    </g>
    
    {/* Right Column - Steps 6 & 7 */}
    {/* Step 6 */}
    <g transform="translate(400, 80)">
      <rect x="0" y="0" width="270" height="120" fill={colors.white} rx="10" stroke={colors.slateLight} strokeWidth="2" />
      <circle cx="35" cy="35" r="22" fill={colors.slateLight} />
      <text x="35" y="41" textAnchor="middle" fill={colors.white} fontSize="14" fontWeight="bold">6</text>
      <text x="70" y="30" fill={colors.secondary} fontSize="12" fontWeight="bold">Keep a Daily Log</text>
      <text x="25" y="60" fill={colors.slate} fontSize="11">• Pain levels</text>
      <text x="25" y="78" fill={colors.slate} fontSize="11">• Medications taken</text>
      <text x="25" y="96" fill={colors.slate} fontSize="11">• Missed work days</text>
      <text x="140" y="60" fill={colors.slate} fontSize="11">• Activity limitations</text>
      <text x="140" y="78" fill={colors.slate} fontSize="11">• Doctor visits</text>
      <text x="140" y="96" fill={colors.slate} fontSize="11">• Expenses incurred</text>
    </g>
    
    {/* Step 7 */}
    <g transform="translate(400, 220)">
      <rect x="0" y="0" width="270" height="120" fill={colors.white} rx="10" stroke={colors.slateLight} strokeWidth="2" />
      <circle cx="35" cy="35" r="22" fill={colors.slateLight} />
      <text x="35" y="41" textAnchor="middle" fill={colors.white} fontSize="14" fontWeight="bold">7</text>
      <text x="70" y="30" fill={colors.secondary} fontSize="12" fontWeight="bold">Preserve Evidence</text>
      <text x="25" y="60" fill={colors.slate} fontSize="11">• Vehicle damage photos</text>
      <text x="25" y="78" fill={colors.slate} fontSize="11">• Injury photos (ongoing)</text>
      <text x="25" y="96" fill={colors.slate} fontSize="11">• Witness contact info</text>
      <text x="140" y="60" fill={colors.slate} fontSize="11">• Scene photos</text>
      <text x="140" y="78" fill={colors.slate} fontSize="11">• Police report</text>
      <text x="140" y="96" fill={colors.slate} fontSize="11">• Medical records</text>
    </g>
    
    {/* Tip Box */}
    <g transform="translate(400, 360)">
      <rect x="0" y="0" width="270" height="70" fill={colors.rose100} rx="10" stroke={colors.primary} strokeWidth="1" strokeDasharray="4" />
      <text x="135" y="25" textAnchor="middle" fill={colors.secondary} fontSize="11" fontWeight="bold">💡 Already gave a statement?</text>
      <text x="135" y="45" textAnchor="middle" fill={colors.slate} fontSize="10">Bring it to me — context helps,</text>
      <text x="135" y="60" textAnchor="middle" fill={colors.slate} fontSize="10">and we can address it head-on.</text>
    </g>
    
    {/* Bottom CTA */}
    <g transform="translate(350, 500)">
      <rect x="-200" y="0" width="400" height="50" fill={colors.ocean} rx="25" />
      <text x="0" y="32" textAnchor="middle" fill={colors.white} fontSize="14" fontWeight="bold">
        📞 Free Consultation: (425) 675-1224
      </text>
    </g>
    
    {/* Arrow marker definition */}
    <defs>
      <marker id="arrowPink" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
        <polygon points="0 0, 10 3.5, 0 7" fill={colors.primary} />
      </marker>
    </defs>
  </svg>
);

// ============================================
// 18. SOCIAL MEDIA RISKS DIAGRAM
// What insurers look for on your profiles
// ============================================
export const SocialMediaRisksDiagram = ({ className = '' }) => (
  <svg 
    viewBox="0 0 700 450" 
    className={`w-full max-w-3xl mx-auto ${className}`}
    role="img"
    aria-label="Diagram showing what insurance companies look for on social media"
  >
    {/* Background */}
    <rect width="700" height="450" fill={colors.rose50} rx="16" />
    
    {/* Title */}
    <text x="350" y="32" textAnchor="middle" fill={colors.secondary} fontSize="16" fontWeight="bold">
      🔍 What Insurance Adjusters Search For on Your Social Media
    </text>
    <text x="350" y="52" textAnchor="middle" fill={colors.slate} fontSize="11">
      They start searching the DAY they get notice of your claim
    </text>
    
    {/* Center - Phone/Social icon */}
    <g transform="translate(350, 180)">
      <rect x="-40" y="-60" width="80" height="120" fill={colors.secondary} rx="12" />
      <rect x="-35" y="-50" width="70" height="90" fill={colors.white} rx="4" />
      <circle cx="0" cy="50" r="8" fill={colors.slateLight} />
      <text x="0" y="-20" textAnchor="middle" fill={colors.secondary} fontSize="10" fontWeight="bold">YOUR</text>
      <text x="0" y="-5" textAnchor="middle" fill={colors.secondary} fontSize="10" fontWeight="bold">PROFILES</text>
      <text x="0" y="15" textAnchor="middle" fill={colors.slate} fontSize="8">FB • IG • TikTok</text>
    </g>
    
    {/* Risk 1 - Top Left */}
    <g transform="translate(130, 130)">
      <rect x="-100" y="-40" width="200" height="80" fill={colors.white} rx="8" stroke="#ef4444" strokeWidth="2" />
      <text x="0" y="-18" textAnchor="middle" fill="#ef4444" fontSize="11" fontWeight="bold">❌ ACTIVITY PHOTOS</text>
      <text x="0" y="0" textAnchor="middle" fill={colors.slate} fontSize="10">Hiking, gym, sports while</text>
      <text x="0" y="14" textAnchor="middle" fill={colors.slate} fontSize="10">claiming mobility limits</text>
      <text x="0" y="32" textAnchor="middle" fill="#ef4444" fontSize="9" fontStyle="italic">"Injuries aren't that bad"</text>
      <line x1="100" y1="0" x2="140" y2="0" stroke="#ef4444" strokeWidth="2" strokeDasharray="4" />
    </g>
    
    {/* Risk 2 - Top Right */}
    <g transform="translate(570, 130)">
      <rect x="-100" y="-40" width="200" height="80" fill={colors.white} rx="8" stroke="#ef4444" strokeWidth="2" />
      <text x="0" y="-18" textAnchor="middle" fill="#ef4444" fontSize="11" fontWeight="bold">❌ CASUAL COMMENTS</text>
      <text x="0" y="0" textAnchor="middle" fill={colors.slate} fontSize="10">"Back to normal!" or</text>
      <text x="0" y="14" textAnchor="middle" fill={colors.slate} fontSize="10">"Feeling great today!"</text>
      <text x="0" y="32" textAnchor="middle" fill="#ef4444" fontSize="9" fontStyle="italic">"Admits recovery"</text>
      <line x1="-100" y1="0" x2="-140" y2="0" stroke="#ef4444" strokeWidth="2" strokeDasharray="4" />
    </g>
    
    {/* Risk 3 - Bottom Left */}
    <g transform="translate(130, 280)">
      <rect x="-100" y="-40" width="200" height="80" fill={colors.white} rx="8" stroke="#ef4444" strokeWidth="2" />
      <text x="0" y="-18" textAnchor="middle" fill="#ef4444" fontSize="11" fontWeight="bold">❌ CHECK-INS & EVENTS</text>
      <text x="0" y="0" textAnchor="middle" fill={colors.slate} fontSize="10">Concerts, travel, parties</text>
      <text x="0" y="14" textAnchor="middle" fill={colors.slate} fontSize="10">while claiming housebound</text>
      <text x="0" y="32" textAnchor="middle" fill="#ef4444" fontSize="9" fontStyle="italic">"Not disabled"</text>
      <line x1="100" y1="0" x2="140" y2="0" stroke="#ef4444" strokeWidth="2" strokeDasharray="4" />
    </g>
    
    {/* Risk 4 - Bottom Right */}
    <g transform="translate(570, 280)">
      <rect x="-100" y="-40" width="200" height="80" fill={colors.white} rx="8" stroke="#ef4444" strokeWidth="2" />
      <text x="0" y="-18" textAnchor="middle" fill="#ef4444" fontSize="11" fontWeight="bold">❌ ACCIDENT STATEMENTS</text>
      <text x="0" y="0" textAnchor="middle" fill={colors.slate} fontSize="10">"lol almost died" or</text>
      <text x="0" y="14" textAnchor="middle" fill={colors.slate} fontSize="10">joking about the crash</text>
      <text x="0" y="32" textAnchor="middle" fill="#ef4444" fontSize="9" fontStyle="italic">"Undermines severity"</text>
      <line x1="-100" y1="0" x2="-140" y2="0" stroke="#ef4444" strokeWidth="2" strokeDasharray="4" />
    </g>
    
    {/* Risk 5 - Bottom Center */}
    <g transform="translate(350, 350)">
      <rect x="-100" y="-25" width="200" height="50" fill={colors.white} rx="8" stroke="#f59e0b" strokeWidth="2" />
      <text x="0" y="-5" textAnchor="middle" fill="#f59e0b" fontSize="11" fontWeight="bold">⚠️ FRIEND/FAMILY TAGS</text>
      <text x="0" y="12" textAnchor="middle" fill={colors.slate} fontSize="10">Others tagging you in activities</text>
      <line x1="0" y1="-25" x2="0" y2="-55" stroke="#f59e0b" strokeWidth="2" strokeDasharray="4" />
    </g>
    
    {/* Warning at bottom */}
    <g transform="translate(350, 420)">
      <rect x="-280" y="-22" width="560" height="44" fill={colors.secondary} rx="8" />
      <text x="0" y="-2" textAnchor="middle" fill={colors.white} fontSize="12" fontWeight="bold">
        ⚖️ Under WA Civil Rule 26(b)(1): Relevant social media CAN become evidence
      </text>
      <text x="0" y="15" textAnchor="middle" fill={colors.primary} fontSize="10">
        Even deleted content can be recovered via subpoenas
      </text>
    </g>
  </svg>
);

// ============================================
// 19. SOCIAL MEDIA TIMELINE IMPACT
// When social media hurts your case most
// ============================================
export const SocialMediaTimelineImpact = ({ className = '' }) => (
  <svg 
    viewBox="0 0 700 300" 
    className={`w-full max-w-3xl mx-auto ${className}`}
    role="img"
    aria-label="Timeline showing when social media posts hurt your case most"
  >
    {/* Background */}
    <rect width="700" height="300" fill={colors.rose50} rx="16" />
    
    {/* Title */}
    <text x="350" y="32" textAnchor="middle" fill={colors.secondary} fontSize="16" fontWeight="bold">
      ⏰ When Social Media Hurts Your Case Most
    </text>
    
    {/* Timeline line */}
    <line x1="60" y1="140" x2="640" y2="140" stroke={colors.secondary} strokeWidth="4" />
    
    {/* Phase 1 - CRITICAL */}
    <g transform="translate(130, 140)">
      <rect x="-60" y="-70" width="120" height="50" fill="#ef4444" rx="8" />
      <text x="0" y="-42" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold">🚨 FIRST 30-90 DAYS</text>
      <text x="0" y="-28" textAnchor="middle" fill="white" fontSize="9">Highest search activity</text>
      <circle cx="0" cy="0" r="15" fill="#ef4444" stroke="white" strokeWidth="3" />
      <text x="0" y="5" textAnchor="middle" fill="white" fontSize="9" fontWeight="bold">!</text>
      <text x="0" y="40" textAnchor="middle" fill={colors.secondary} fontSize="10" fontWeight="bold">CRITICAL</text>
      <text x="0" y="55" textAnchor="middle" fill={colors.slate} fontSize="9">Most damage happens</text>
      <text x="0" y="68" textAnchor="middle" fill={colors.slate} fontSize="9">here</text>
    </g>
    
    {/* Phase 2 - HIGH RISK */}
    <g transform="translate(300, 140)">
      <rect x="-65" y="-70" width="130" height="50" fill="#f59e0b" rx="8" />
      <text x="0" y="-42" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold">⚠️ DURING TREATMENT</text>
      <text x="0" y="-28" textAnchor="middle" fill="white" fontSize="9">3-12 months</text>
      <circle cx="0" cy="0" r="15" fill="#f59e0b" stroke="white" strokeWidth="3" />
      <text x="0" y="5" textAnchor="middle" fill="white" fontSize="9" fontWeight="bold">!</text>
      <text x="0" y="40" textAnchor="middle" fill={colors.secondary} fontSize="10" fontWeight="bold">HIGH RISK</text>
      <text x="0" y="55" textAnchor="middle" fill={colors.slate} fontSize="9">Activity vs. claimed</text>
      <text x="0" y="68" textAnchor="middle" fill={colors.slate} fontSize="9">limitations</text>
    </g>
    
    {/* Phase 3 - MODERATE */}
    <g transform="translate(470, 140)">
      <rect x="-60" y="-70" width="120" height="50" fill={colors.primary} rx="8" />
      <text x="0" y="-42" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold">📋 PRE-SETTLEMENT</text>
      <text x="0" y="-28" textAnchor="middle" fill="white" fontSize="9">Negotiation phase</text>
      <circle cx="0" cy="0" r="15" fill={colors.primary} stroke="white" strokeWidth="3" />
      <text x="0" y="5" textAnchor="middle" fill="white" fontSize="10">~</text>
      <text x="0" y="40" textAnchor="middle" fill={colors.secondary} fontSize="10" fontWeight="bold">MODERATE</text>
      <text x="0" y="55" textAnchor="middle" fill={colors.slate} fontSize="9">Old posts resurface</text>
      <text x="0" y="68" textAnchor="middle" fill={colors.slate} fontSize="9">for settlement talks</text>
    </g>
    
    {/* Phase 4 - IF TRIAL */}
    <g transform="translate(600, 140)">
      <rect x="-50" y="-70" width="100" height="50" fill={colors.ocean} rx="8" />
      <text x="0" y="-42" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold">⚖️ AT TRIAL</text>
      <text x="0" y="-28" textAnchor="middle" fill="white" fontSize="9">If litigation</text>
      <circle cx="0" cy="0" r="15" fill={colors.ocean} stroke="white" strokeWidth="3" />
      <text x="0" y="5" textAnchor="middle" fill="white" fontSize="10">!</text>
      <text x="0" y="40" textAnchor="middle" fill={colors.secondary} fontSize="10" fontWeight="bold">JURY SEES ALL</text>
      <text x="0" y="55" textAnchor="middle" fill={colors.slate} fontSize="9">Credibility on</text>
      <text x="0" y="68" textAnchor="middle" fill={colors.slate} fontSize="9">the line</text>
    </g>
    
    {/* Bottom note */}
    <g transform="translate(350, 265)">
      <rect x="-250" y="-20" width="500" height="40" fill={colors.white} rx="8" stroke={colors.secondary} strokeWidth="1" />
      <text x="0" y="0" textAnchor="middle" fill={colors.secondary} fontSize="11" fontWeight="bold">
        📌 Statute of Limitations: 3 years in WA — protection needs to last
      </text>
      <text x="0" y="16" textAnchor="middle" fill={colors.slate} fontSize="10">
        Consider going dark for at least the first 6-12 months
      </text>
    </g>
  </svg>
);

// ============================================
// 20. SOCIAL MEDIA PROTECTION STEPS
// How to protect your claim
// ============================================
export const SocialMediaProtectionSteps = ({ className = '' }) => (
  <svg 
    viewBox="0 0 700 520" 
    className={`w-full max-w-3xl mx-auto ${className}`}
    role="img"
    aria-label="Step-by-step guide to protect your case from social media damage"
  >
    {/* Background */}
    <rect width="700" height="520" fill={colors.rose50} rx="16" />
    
    {/* Title */}
    <text x="350" y="32" textAnchor="middle" fill={colors.secondary} fontSize="16" fontWeight="bold">
      🛡️ Social Media Protection Plan — Do This Now
    </text>
    
    {/* Steps in two columns */}
    {/* Column 1 */}
    <g transform="translate(50, 70)">
      {/* Step 1 */}
      <g transform="translate(0, 0)">
        <circle cx="25" cy="20" r="20" fill={colors.ocean} />
        <text x="25" y="26" textAnchor="middle" fill="white" fontSize="14" fontWeight="bold">1</text>
        <text x="55" y="15" fill={colors.secondary} fontSize="11" fontWeight="bold">SET ALL PROFILES TO PRIVATE</text>
        <text x="55" y="30" fill={colors.slate} fontSize="10">FB, IG, TikTok, Snapchat, Twitter, LinkedIn</text>
      </g>
      
      {/* Step 2 */}
      <g transform="translate(0, 60)">
        <circle cx="25" cy="20" r="20" fill={colors.ocean} />
        <text x="25" y="26" textAnchor="middle" fill="white" fontSize="14" fontWeight="bold">2</text>
        <text x="55" y="15" fill={colors.secondary} fontSize="11" fontWeight="bold">STOP POSTING ABOUT:</text>
        <text x="55" y="30" fill={colors.slate} fontSize="10">Accident, injuries, treatment, daily activities</text>
      </g>
      
      {/* Step 3 */}
      <g transform="translate(0, 120)">
        <circle cx="25" cy="20" r="20" fill={colors.ocean} />
        <text x="25" y="26" textAnchor="middle" fill="white" fontSize="14" fontWeight="bold">3</text>
        <text x="55" y="15" fill={colors.secondary} fontSize="11" fontWeight="bold">NO CASE DISCUSSION</text>
        <text x="55" y="30" fill={colors.slate} fontSize="10">Lawyers, insurance, settlements, fault</text>
      </g>
      
      {/* Step 4 */}
      <g transform="translate(0, 180)">
        <circle cx="25" cy="20" r="20" fill={colors.ocean} />
        <text x="25" y="26" textAnchor="middle" fill="white" fontSize="14" fontWeight="bold">4</text>
        <text x="55" y="15" fill={colors.secondary} fontSize="11" fontWeight="bold">TELL FRIENDS & FAMILY</text>
        <text x="55" y="30" fill={colors.slate} fontSize="10">Don't tag you or post about you</text>
      </g>
    </g>
    
    {/* Column 2 */}
    <g transform="translate(350, 70)">
      {/* Step 5 */}
      <g transform="translate(0, 0)">
        <circle cx="25" cy="20" r="20" fill={colors.primary} />
        <text x="25" y="26" textAnchor="middle" fill="white" fontSize="14" fontWeight="bold">5</text>
        <text x="55" y="15" fill={colors.secondary} fontSize="11" fontWeight="bold">CONSIDER DEACTIVATION</text>
        <text x="55" y="30" fill={colors.slate} fontSize="10">Go dark for first 6-12 months</text>
      </g>
      
      {/* Step 6 */}
      <g transform="translate(0, 60)">
        <circle cx="25" cy="20" r="20" fill={colors.primary} />
        <text x="25" y="26" textAnchor="middle" fill="white" fontSize="14" fontWeight="bold">6</text>
        <text x="55" y="15" fill={colors.secondary} fontSize="11" fontWeight="bold">REJECT STRANGER REQUESTS</text>
        <text x="55" y="30" fill={colors.slate} fontSize="10">Could be investigators</text>
      </g>
      
      {/* Step 7 */}
      <g transform="translate(0, 120)">
        <circle cx="25" cy="20" r="20" fill={colors.primary} />
        <text x="25" y="26" textAnchor="middle" fill="white" fontSize="14" fontWeight="bold">7</text>
        <text x="55" y="15" fill={colors.secondary} fontSize="11" fontWeight="bold">IF ALREADY POSTED:</text>
        <text x="55" y="30" fill={colors.slate} fontSize="10">Screenshot for context, tell attorney</text>
      </g>
      
      {/* Step 8 */}
      <g transform="translate(0, 180)">
        <circle cx="25" cy="20" r="20" fill={colors.primary} />
        <text x="25" y="26" textAnchor="middle" fill="white" fontSize="14" fontWeight="bold">8</text>
        <text x="55" y="15" fill={colors.secondary} fontSize="11" fontWeight="bold">SKIP STORIES TOO</text>
        <text x="55" y="30" fill={colors.slate} fontSize="10">They disappear but screenshots don't</text>
      </g>
    </g>
    
    {/* If damage is done section */}
    <g transform="translate(350, 330)">
      <rect x="-300" y="0" width="600" height="100" fill={colors.white} rx="10" stroke={colors.secondary} strokeWidth="2" />
      <text x="0" y="25" textAnchor="middle" fill={colors.secondary} fontSize="13" fontWeight="bold">
        💡 If Damage Is Already Done — Don't Panic
      </text>
      <text x="0" y="48" textAnchor="middle" fill={colors.slate} fontSize="11">
        • Explain timing ("photo from a good hour amid bad days")
      </text>
      <text x="0" y="65" textAnchor="middle" fill={colors.slate} fontSize="11">
        • Medical records show fluctuations
      </text>
      <text x="0" y="82" textAnchor="middle" fill={colors.slate} fontSize="11">
        • We argue relevance/proportionality to limit what comes in
      </text>
    </g>
    
    {/* Bottom CTA */}
    <g transform="translate(350, 475)">
      <rect x="-200" y="0" width="400" height="45" fill={colors.ocean} rx="22" />
      <text x="0" y="28" textAnchor="middle" fill="white" fontSize="12" fontWeight="bold">
        📞 Worried about past posts? Call (425) 675-1224
      </text>
    </g>
  </svg>
);

// ============================================
// 14. CROSSWALK LAW DIAGRAM
// RCW 46.61.235 visualization
// ============================================
export const CrosswalkLawDiagram = ({ className = '' }) => (
  <svg 
    viewBox="0 0 700 400" 
    className={`w-full max-w-3xl mx-auto ${className}`}
    role="img"
    aria-label="Washington crosswalk law diagram showing when drivers must stop"
  >
    {/* Background */}
    <rect width="700" height="400" fill={colors.rose50} rx="16" />
    
    {/* Title */}
    <text x="350" y="32" textAnchor="middle" fill={colors.secondary} fontSize="16" fontWeight="bold">
      ⚖️ Washington Crosswalk Law (RCW 46.61.235)
    </text>
    <text x="350" y="52" textAnchor="middle" fill={colors.slate} fontSize="11">
      Drivers MUST stop and remain stopped when pedestrians are in a crosswalk
    </text>
    
    {/* Road illustration */}
    <rect x="50" y="120" width="600" height="140" fill="#4a5568" rx="4" />
    
    {/* Road lines */}
    <line x1="350" y1="120" x2="350" y2="260" stroke="#fbbf24" strokeWidth="3" strokeDasharray="20 10" />
    
    {/* Crosswalk stripes */}
    <g>
      <rect x="280" y="120" width="15" height="140" fill="white" />
      <rect x="305" y="120" width="15" height="140" fill="white" />
      <rect x="330" y="120" width="15" height="140" fill="white" />
      <rect x="355" y="120" width="15" height="140" fill="white" />
      <rect x="380" y="120" width="15" height="140" fill="white" />
      <rect x="405" y="120" width="15" height="140" fill="white" />
    </g>
    
    {/* Pedestrian icon */}
    <g transform="translate(350, 175)">
      <circle cx="0" cy="-20" r="12" fill={colors.ocean} />
      <line x1="0" y1="-8" x2="0" y2="20" stroke={colors.ocean} strokeWidth="4" />
      <line x1="-15" y1="5" x2="15" y2="5" stroke={colors.ocean} strokeWidth="4" />
      <line x1="0" y1="20" x2="-12" y2="40" stroke={colors.ocean} strokeWidth="4" />
      <line x1="0" y1="20" x2="12" y2="40" stroke={colors.ocean} strokeWidth="4" />
    </g>
    
    {/* Car icon (left side - must stop) */}
    <g transform="translate(150, 190)">
      <rect x="-40" y="-20" width="80" height="40" fill="#ef4444" rx="8" />
      <rect x="-35" y="-15" width="25" height="15" fill="#1e293b" rx="3" />
      <rect x="10" y="-15" width="25" height="15" fill="#1e293b" rx="3" />
      <circle cx="-25" cy="25" r="10" fill="#1e293b" />
      <circle cx="25" cy="25" r="10" fill="#1e293b" />
    </g>
    
    {/* STOP indicator */}
    <g transform="translate(150, 130)">
      <rect x="-35" y="-18" width="70" height="36" fill="#ef4444" rx="6" />
      <text x="0" y="6" textAnchor="middle" fill="white" fontSize="14" fontWeight="bold">STOP</text>
    </g>
    
    {/* Zone explanation - left */}
    <g transform="translate(100, 295)">
      <rect x="-60" y="-15" width="120" height="55" fill={colors.white} rx="6" stroke="#ef4444" strokeWidth="2" />
      <text x="0" y="5" textAnchor="middle" fill={colors.secondary} fontSize="10" fontWeight="bold">Driver's Half</text>
      <text x="0" y="20" textAnchor="middle" fill="#ef4444" fontSize="10" fontWeight="bold">MUST STOP</text>
      <text x="0" y="33" textAnchor="middle" fill={colors.slate} fontSize="9">when pedestrian</text>
      <text x="0" y="44" textAnchor="middle" fill={colors.slate} fontSize="9">within one lane</text>
    </g>
    
    {/* Zone explanation - middle */}
    <g transform="translate(350, 295)">
      <rect x="-80" y="-15" width="160" height="55" fill={colors.white} rx="6" stroke={colors.ocean} strokeWidth="2" />
      <text x="0" y="5" textAnchor="middle" fill={colors.secondary} fontSize="10" fontWeight="bold">Crosswalk Zone</text>
      <text x="0" y="20" textAnchor="middle" fill={colors.ocean} fontSize="10" fontWeight="bold">PEDESTRIAN PROTECTED</text>
      <text x="0" y="33" textAnchor="middle" fill={colors.slate} fontSize="9">Marked OR unmarked</text>
      <text x="0" y="44" textAnchor="middle" fill={colors.slate} fontSize="9">at any intersection</text>
    </g>
    
    {/* Zone explanation - right */}
    <g transform="translate(600, 295)">
      <rect x="-60" y="-15" width="120" height="55" fill={colors.white} rx="6" stroke={colors.slateLight} strokeWidth="2" />
      <text x="0" y="5" textAnchor="middle" fill={colors.secondary} fontSize="10" fontWeight="bold">Opposite Half</text>
      <text x="0" y="20" textAnchor="middle" fill={colors.slate} fontSize="10">Prepare to stop</text>
      <text x="0" y="33" textAnchor="middle" fill={colors.slate} fontSize="9">as pedestrian</text>
      <text x="0" y="44" textAnchor="middle" fill={colors.slate} fontSize="9">approaches</text>
    </g>
    
    {/* Key point at bottom */}
    <g transform="translate(350, 378)">
      <text x="0" y="0" textAnchor="middle" fill={colors.secondary} fontSize="11" fontWeight="bold">
        ⚠️ Exception: Pedestrians cannot suddenly dart into path of close vehicle
      </text>
    </g>
  </svg>
);

// ============================================
// 15. COMPARATIVE NEGLIGENCE CHART
// How fault percentages affect recovery
// ============================================
export const ComparativeNegligenceChart = ({ className = '' }) => (
  <svg 
    viewBox="0 0 700 320" 
    className={`w-full max-w-3xl mx-auto ${className}`}
    role="img"
    aria-label="Washington comparative negligence chart showing how fault affects recovery"
  >
    {/* Background */}
    <rect width="700" height="320" fill={colors.rose50} rx="16" />
    
    {/* Title */}
    <text x="350" y="32" textAnchor="middle" fill={colors.secondary} fontSize="16" fontWeight="bold">
      💡 Washington Comparative Negligence (RCW 4.22.005)
    </text>
    <text x="350" y="52" textAnchor="middle" fill={colors.slate} fontSize="11">
      You can still recover damages even if you're partially at fault
    </text>
    
    {/* Example scenarios */}
    {/* Scenario 1 */}
    <g transform="translate(130, 130)">
      <rect x="-100" y="-50" width="200" height="130" fill={colors.white} rx="10" stroke={colors.ocean} strokeWidth="2" />
      <text x="0" y="-30" textAnchor="middle" fill={colors.ocean} fontSize="13" fontWeight="bold">0% Your Fault</text>
      <text x="0" y="-10" textAnchor="middle" fill={colors.slate} fontSize="11">$100,000 damages</text>
      <line x1="-60" y1="5" x2="60" y2="5" stroke={colors.slateLight} strokeWidth="1" />
      <text x="0" y="30" textAnchor="middle" fill={colors.secondary} fontSize="22" fontWeight="bold">$100,000</text>
      <text x="0" y="50" textAnchor="middle" fill={colors.ocean} fontSize="11">Full Recovery</text>
      <text x="0" y="68" textAnchor="middle" fill={colors.slate} fontSize="10">✓ 100% of damages</text>
    </g>
    
    {/* Scenario 2 */}
    <g transform="translate(350, 130)">
      <rect x="-100" y="-50" width="200" height="130" fill={colors.white} rx="10" stroke={colors.primary} strokeWidth="2" />
      <text x="0" y="-30" textAnchor="middle" fill={colors.primary} fontSize="13" fontWeight="bold">20% Your Fault</text>
      <text x="0" y="-10" textAnchor="middle" fill={colors.slate} fontSize="11">$100,000 damages</text>
      <line x1="-60" y1="5" x2="60" y2="5" stroke={colors.slateLight} strokeWidth="1" />
      <text x="0" y="30" textAnchor="middle" fill={colors.secondary} fontSize="22" fontWeight="bold">$80,000</text>
      <text x="0" y="50" textAnchor="middle" fill={colors.primary} fontSize="11">Reduced Recovery</text>
      <text x="0" y="68" textAnchor="middle" fill={colors.slate} fontSize="10">80% of damages</text>
    </g>
    
    {/* Scenario 3 */}
    <g transform="translate(570, 130)">
      <rect x="-100" y="-50" width="200" height="130" fill={colors.white} rx="10" stroke="#f59e0b" strokeWidth="2" />
      <text x="0" y="-30" textAnchor="middle" fill="#f59e0b" fontSize="13" fontWeight="bold">50% Your Fault</text>
      <text x="0" y="-10" textAnchor="middle" fill={colors.slate} fontSize="11">$100,000 damages</text>
      <line x1="-60" y1="5" x2="60" y2="5" stroke={colors.slateLight} strokeWidth="1" />
      <text x="0" y="30" textAnchor="middle" fill={colors.secondary} fontSize="22" fontWeight="bold">$50,000</text>
      <text x="0" y="50" textAnchor="middle" fill="#f59e0b" fontSize="11">Still Recoverable!</text>
      <text x="0" y="68" textAnchor="middle" fill={colors.slate} fontSize="10">50% of damages</text>
    </g>
    
    {/* Bottom callout */}
    <g transform="translate(350, 280)">
      <rect x="-280" y="-25" width="560" height="50" fill={colors.secondary} rx="8" />
      <text x="0" y="-2" textAnchor="middle" fill={colors.white} fontSize="12" fontWeight="bold">
        ✓ Key: Washington allows recovery at ANY fault percentage under 100%
      </text>
      <text x="0" y="16" textAnchor="middle" fill={colors.primary} fontSize="11">
        Even if you were distracted, you may still be entitled to significant compensation
      </text>
    </g>
  </svg>
);

// ============================================
// 16. PEDESTRIAN EVIDENCE CHECKLIST
// What to gather after a crosswalk accident
// ============================================
export const PedestrianEvidenceChecklist = ({ className = '' }) => (
  <svg 
    viewBox="0 0 700 420" 
    className={`w-full max-w-3xl mx-auto ${className}`}
    role="img"
    aria-label="Evidence checklist for pedestrian accident claims"
  >
    {/* Background */}
    <rect width="700" height="420" fill={colors.rose50} rx="16" />
    
    {/* Title */}
    <text x="350" y="32" textAnchor="middle" fill={colors.secondary} fontSize="16" fontWeight="bold">
      📋 Evidence Checklist: Strengthen Your Pedestrian Claim
    </text>
    
    {/* Urgency banner */}
    <g transform="translate(350, 60)">
      <rect x="-200" y="-12" width="400" height="24" fill="#ef4444" rx="12" />
      <text x="0" y="5" textAnchor="middle" fill="white" fontSize="11" fontWeight="bold">
        ⏰ Act Fast — Evidence fades within days, camera footage gets deleted
      </text>
    </g>
    
    {/* Column 1 - Immediate */}
    <g transform="translate(130, 120)">
      <rect x="-110" y="-25" width="220" height="210" fill={colors.white} rx="10" stroke={colors.ocean} strokeWidth="2" />
      <rect x="-110" y="-25" width="220" height="35" fill={colors.ocean} rx="10" />
      <rect x="-110" y="5" width="220" height="5" fill={colors.ocean} />
      <text x="0" y="-2" textAnchor="middle" fill="white" fontSize="12" fontWeight="bold">🚨 IMMEDIATE</text>
      
      <g transform="translate(-95, 30)">
        <rect x="0" y="0" width="16" height="16" fill={colors.rose100} rx="3" stroke={colors.ocean} strokeWidth="1" />
        <text x="24" y="13" fill={colors.secondary} fontSize="11">Police report</text>
      </g>
      <g transform="translate(-95, 55)">
        <rect x="0" y="0" width="16" height="16" fill={colors.rose100} rx="3" stroke={colors.ocean} strokeWidth="1" />
        <text x="24" y="13" fill={colors.secondary} fontSize="11">Scene photos</text>
      </g>
      <g transform="translate(-95, 80)">
        <rect x="0" y="0" width="16" height="16" fill={colors.rose100} rx="3" stroke={colors.ocean} strokeWidth="1" />
        <text x="24" y="13" fill={colors.secondary} fontSize="11">Crosswalk markings</text>
      </g>
      <g transform="translate(-95, 105)">
        <rect x="0" y="0" width="16" height="16" fill={colors.rose100} rx="3" stroke={colors.ocean} strokeWidth="1" />
        <text x="24" y="13" fill={colors.secondary} fontSize="11">Vehicle damage</text>
      </g>
      <g transform="translate(-95, 130)">
        <rect x="0" y="0" width="16" height="16" fill={colors.rose100} rx="3" stroke={colors.ocean} strokeWidth="1" />
        <text x="24" y="13" fill={colors.secondary} fontSize="11">Your injuries</text>
      </g>
      <g transform="translate(-95, 155)">
        <rect x="0" y="0" width="16" height="16" fill={colors.rose100} rx="3" stroke={colors.ocean} strokeWidth="1" />
        <text x="24" y="13" fill={colors.secondary} fontSize="11">Clothing worn</text>
      </g>
    </g>
    
    {/* Column 2 - Within 48 Hours */}
    <g transform="translate(350, 120)">
      <rect x="-100" y="-25" width="200" height="210" fill={colors.white} rx="10" stroke={colors.primary} strokeWidth="2" />
      <rect x="-100" y="-25" width="200" height="35" fill={colors.primary} rx="10" />
      <rect x="-100" y="5" width="200" height="5" fill={colors.primary} />
      <text x="0" y="-2" textAnchor="middle" fill="white" fontSize="12" fontWeight="bold">📅 WITHIN 48 HRS</text>
      
      <g transform="translate(-85, 30)">
        <rect x="0" y="0" width="16" height="16" fill={colors.rose100} rx="3" stroke={colors.primary} strokeWidth="1" />
        <text x="24" y="13" fill={colors.secondary} fontSize="11">Witness contacts</text>
      </g>
      <g transform="translate(-85, 55)">
        <rect x="0" y="0" width="16" height="16" fill={colors.rose100} rx="3" stroke={colors.primary} strokeWidth="1" />
        <text x="24" y="13" fill={colors.secondary} fontSize="11">Security camera</text>
      </g>
      <g transform="translate(-85, 80)">
        <rect x="0" y="0" width="16" height="16" fill={colors.rose100} rx="3" stroke={colors.primary} strokeWidth="1" />
        <text x="24" y="13" fill={colors.secondary} fontSize="11">requests</text>
      </g>
      <g transform="translate(-85, 105)">
        <rect x="0" y="0" width="16" height="16" fill={colors.rose100} rx="3" stroke={colors.primary} strokeWidth="1" />
        <text x="24" y="13" fill={colors.secondary} fontSize="11">Traffic cam footage</text>
      </g>
      <g transform="translate(-85, 130)">
        <rect x="0" y="0" width="16" height="16" fill={colors.rose100} rx="3" stroke={colors.primary} strokeWidth="1" />
        <text x="24" y="13" fill={colors.secondary} fontSize="11">Medical records</text>
      </g>
      <g transform="translate(-85, 155)">
        <rect x="0" y="0" width="16" height="16" fill={colors.rose100} rx="3" stroke={colors.primary} strokeWidth="1" />
        <text x="24" y="13" fill={colors.secondary} fontSize="11">Hit-and-run report</text>
      </g>
    </g>
    
    {/* Column 3 - Document */}
    <g transform="translate(570, 120)">
      <rect x="-100" y="-25" width="200" height="210" fill={colors.white} rx="10" stroke={colors.slateLight} strokeWidth="2" />
      <rect x="-100" y="-25" width="200" height="35" fill={colors.slate} rx="10" />
      <rect x="-100" y="5" width="200" height="5" fill={colors.slate} />
      <text x="0" y="-2" textAnchor="middle" fill="white" fontSize="12" fontWeight="bold">📝 DOCUMENT</text>
      
      <g transform="translate(-85, 30)">
        <rect x="0" y="0" width="16" height="16" fill={colors.rose100} rx="3" stroke={colors.slate} strokeWidth="1" />
        <text x="24" y="13" fill={colors.secondary} fontSize="11">Time of day</text>
      </g>
      <g transform="translate(-85, 55)">
        <rect x="0" y="0" width="16" height="16" fill={colors.rose100} rx="3" stroke={colors.slate} strokeWidth="1" />
        <text x="24" y="13" fill={colors.secondary} fontSize="11">Lighting conditions</text>
      </g>
      <g transform="translate(-85, 80)">
        <rect x="0" y="0" width="16" height="16" fill={colors.rose100} rx="3" stroke={colors.slate} strokeWidth="1" />
        <text x="24" y="13" fill={colors.secondary} fontSize="11">Weather</text>
      </g>
      <g transform="translate(-85, 105)">
        <rect x="0" y="0" width="16" height="16" fill={colors.rose100} rx="3" stroke={colors.slate} strokeWidth="1" />
        <text x="24" y="13" fill={colors.secondary} fontSize="11">Visibility factors</text>
      </g>
      <g transform="translate(-85, 130)">
        <rect x="0" y="0" width="16" height="16" fill={colors.rose100} rx="3" stroke={colors.slate} strokeWidth="1" />
        <text x="24" y="13" fill={colors.secondary} fontSize="11">Your activity</text>
      </g>
      <g transform="translate(-85, 155)">
        <rect x="0" y="0" width="16" height="16" fill={colors.rose100} rx="3" stroke={colors.slate} strokeWidth="1" />
        <text x="24" y="13" fill={colors.secondary} fontSize="11">Driver behavior</text>
      </g>
    </g>
    
    {/* Bottom note */}
    <g transform="translate(350, 370)">
      <rect x="-280" y="-25" width="560" height="50" fill={colors.white} rx="8" stroke={colors.secondary} strokeWidth="1" />
      <text x="0" y="-2" textAnchor="middle" fill={colors.secondary} fontSize="11" fontWeight="bold">
        💡 Insurance often claims "darted out" or "poor visibility"
      </text>
      <text x="0" y="16" textAnchor="middle" fill={colors.slate} fontSize="11">
        Solid evidence from this checklist counters those defenses
      </text>
    </g>
  </svg>
);

// ============================================
// 17. PEDESTRIAN CLAIM TIMELINE
// Key deadlines and time-sensitive actions
// ============================================
export const PedestrianClaimTimeline = ({ className = '' }) => (
  <svg 
    viewBox="0 0 700 280" 
    className={`w-full max-w-3xl mx-auto ${className}`}
    role="img"
    aria-label="Timeline for pedestrian injury claims in Washington"
  >
    {/* Background */}
    <rect width="700" height="280" fill={colors.rose50} rx="16" />
    
    {/* Title */}
    <text x="350" y="32" textAnchor="middle" fill={colors.secondary} fontSize="16" fontWeight="bold">
      ⏱️ Critical Timeline: Don't Miss These Deadlines
    </text>
    
    {/* Timeline line */}
    <line x1="80" y1="130" x2="620" y2="130" stroke={colors.secondary} strokeWidth="4" />
    
    {/* Point 1 - Immediate */}
    <g transform="translate(120, 130)">
      <circle cx="0" cy="0" r="18" fill="#ef4444" stroke="white" strokeWidth="3" />
      <text x="0" y="5" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold">!</text>
      <text x="0" y="-35" textAnchor="middle" fill="#ef4444" fontSize="11" fontWeight="bold">IMMEDIATE</text>
      <text x="0" y="45" textAnchor="middle" fill={colors.secondary} fontSize="10">Call 911</text>
      <text x="0" y="58" textAnchor="middle" fill={colors.slate} fontSize="9">Get medical care</text>
    </g>
    
    {/* Point 2 - 72 Hours */}
    <g transform="translate(260, 130)">
      <circle cx="0" cy="0" r="18" fill="#f59e0b" stroke="white" strokeWidth="3" />
      <text x="0" y="5" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold">72h</text>
      <text x="0" y="-35" textAnchor="middle" fill="#f59e0b" fontSize="11" fontWeight="bold">72 HOURS</text>
      <text x="0" y="45" textAnchor="middle" fill={colors.secondary} fontSize="10">Hit-and-run</text>
      <text x="0" y="58" textAnchor="middle" fill={colors.slate} fontSize="9">report deadline</text>
    </g>
    
    {/* Point 3 - Days */}
    <g transform="translate(400, 130)">
      <circle cx="0" cy="0" r="18" fill={colors.primary} stroke="white" strokeWidth="3" />
      <text x="0" y="5" textAnchor="middle" fill="white" fontSize="9" fontWeight="bold">DAYS</text>
      <text x="0" y="-35" textAnchor="middle" fill={colors.primary} fontSize="11" fontWeight="bold">WITHIN DAYS</text>
      <text x="0" y="45" textAnchor="middle" fill={colors.secondary} fontSize="10">Gather evidence</text>
      <text x="0" y="58" textAnchor="middle" fill={colors.slate} fontSize="9">Camera footage</text>
    </g>
    
    {/* Point 4 - 3 Years */}
    <g transform="translate(540, 130)">
      <circle cx="0" cy="0" r="18" fill={colors.ocean} stroke="white" strokeWidth="3" />
      <text x="0" y="5" textAnchor="middle" fill="white" fontSize="9" fontWeight="bold">3YR</text>
      <text x="0" y="-35" textAnchor="middle" fill={colors.ocean} fontSize="11" fontWeight="bold">3 YEARS</text>
      <text x="0" y="45" textAnchor="middle" fill={colors.secondary} fontSize="10">Statute of</text>
      <text x="0" y="58" textAnchor="middle" fill={colors.slate} fontSize="9">limitations</text>
    </g>
    
    {/* Arrows showing urgency decreasing */}
    <g transform="translate(350, 200)">
      <rect x="-250" y="0" width="500" height="8" fill="url(#urgencyGradient)" rx="4" />
      <text x="-230" y="30" fill="#ef4444" fontSize="10" fontWeight="bold">MOST URGENT</text>
      <text x="180" y="30" fill={colors.ocean} fontSize="10">Final Deadline</text>
    </g>
    
    {/* Bottom note */}
    <g transform="translate(350, 255)">
      <text x="0" y="0" textAnchor="middle" fill={colors.secondary} fontSize="11" fontWeight="bold">
        RCW 4.16.080: You have 3 years to file, but evidence quality degrades fast
      </text>
    </g>
    
    {/* Gradient definition */}
    <defs>
      <linearGradient id="urgencyGradient" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#ef4444" />
        <stop offset="50%" stopColor={colors.primary} />
        <stop offset="100%" stopColor={colors.ocean} />
      </linearGradient>
    </defs>
  </svg>
);

// ============================================
// 13. WASHINGTON CLAIMS HANDLING LAW
// WAC 284-30-330 requirements
// ============================================
export const WashingtonClaimsLawGraphic = ({ className = '' }) => (
  <svg 
    viewBox="0 0 700 320" 
    className={`w-full max-w-3xl mx-auto ${className}`}
    role="img"
    aria-label="Washington state fair claims handling law requirements"
  >
    {/* Background */}
    <rect width="700" height="320" fill="#f0fdfa" rx="16" />
    
    {/* Title with legal icon */}
    <text x="350" y="35" textAnchor="middle" fill={colors.secondary} fontSize="18" fontWeight="bold">
      ⚖️ Washington Law Protects You (WAC 284-30-330)
    </text>
    <text x="350" y="55" textAnchor="middle" fill={colors.slate} fontSize="12">
      Insurance companies must handle claims in good faith. These practices are UNFAIR:
    </text>
    
    {/* Three violation boxes */}
    <g transform="translate(130, 150)">
      <rect x="-100" y="-60" width="200" height="120" fill={colors.white} rx="10" stroke="#ef4444" strokeWidth="2" />
      <text x="0" y="-35" textAnchor="middle" fill="#ef4444" fontSize="28">🚫</text>
      <text x="0" y="-5" textAnchor="middle" fill={colors.secondary} fontSize="12" fontWeight="bold">Failing to respond</text>
      <text x="0" y="12" textAnchor="middle" fill={colors.secondary} fontSize="12" fontWeight="bold">promptly</text>
      <text x="0" y="35" textAnchor="middle" fill={colors.slate} fontSize="10">Must acknowledge your</text>
      <text x="0" y="48" textAnchor="middle" fill={colors.slate} fontSize="10">communications quickly</text>
    </g>
    
    <g transform="translate(350, 150)">
      <rect x="-100" y="-60" width="200" height="120" fill={colors.white} rx="10" stroke="#ef4444" strokeWidth="2" />
      <text x="0" y="-35" textAnchor="middle" fill="#ef4444" fontSize="28">🚫</text>
      <text x="0" y="-5" textAnchor="middle" fill={colors.secondary} fontSize="12" fontWeight="bold">Refusing to pay without</text>
      <text x="0" y="12" textAnchor="middle" fill={colors.secondary} fontSize="12" fontWeight="bold">reasonable investigation</text>
      <text x="0" y="35" textAnchor="middle" fill={colors.slate} fontSize="10">Can't deny claims without</text>
      <text x="0" y="48" textAnchor="middle" fill={colors.slate} fontSize="10">proper review</text>
    </g>
    
    <g transform="translate(570, 150)">
      <rect x="-100" y="-60" width="200" height="120" fill={colors.white} rx="10" stroke="#ef4444" strokeWidth="2" />
      <text x="0" y="-35" textAnchor="middle" fill="#ef4444" fontSize="28">🚫</text>
      <text x="0" y="-5" textAnchor="middle" fill={colors.secondary} fontSize="12" fontWeight="bold">Compelling statements</text>
      <text x="0" y="12" textAnchor="middle" fill={colors.secondary} fontSize="12" fontWeight="bold">not in your interest</text>
      <text x="0" y="35" textAnchor="middle" fill={colors.slate} fontSize="10">You control what and</text>
      <text x="0" y="48" textAnchor="middle" fill={colors.slate} fontSize="10">when you share</text>
    </g>
    
    {/* Bottom callout */}
    <g transform="translate(350, 285)">
      <rect x="-280" y="-25" width="560" height="50" fill={colors.ocean} rx="8" />
      <text x="0" y="0" textAnchor="middle" fill={colors.white} fontSize="13" fontWeight="bold">
        ✓ YOU ARE NOT REQUIRED to give a recorded statement to their insurer
      </text>
      <text x="0" y="18" textAnchor="middle" fill={colors.oceanLight} fontSize="11">
        — Washington State Office of the Insurance Commissioner
      </text>
    </g>
  </svg>
);

// ============================================
// PUBLIC SAFETY WORKER GRAPHICS
// For firefighter, EMS, WSDOT, and public works articles
// ============================================

// Firefighter-specific colors
const fireColors = {
  fireRed: '#dc2626',
  fireRedLight: '#fecaca',
  fireOrange: '#ea580c',
};

// ============================================
// COMPARATIVE FAULT GRAPHICS
// For Washington comparative fault law article
// ============================================

// ============================================
// 29. COMPARATIVE FAULT EXAMPLES CHART
// Shows how fault % reduces recovery with real examples
// ============================================
export const ComparativeFaultExamplesChart = ({ className = '' }) => (
  <svg 
    viewBox="0 0 700 420" 
    className={`w-full max-w-3xl mx-auto ${className}`}
    role="img"
    aria-label="Chart showing how comparative fault percentages reduce injury settlements"
  >
    {/* Background */}
    <rect width="700" height="420" fill={colors.rose50} rx="16" />
    
    {/* Title */}
    <text x="350" y="35" textAnchor="middle" fill={colors.secondary} fontSize="18" fontWeight="bold">
      How Comparative Fault Reduces Your Recovery
    </text>
    <text x="350" y="55" textAnchor="middle" fill={colors.slate} fontSize="12">
      Your settlement is reduced by your percentage of fault
    </text>
    
    {/* Example 1: 10% fault */}
    <g transform="translate(50, 90)">
      <rect x="0" y="0" width="180" height="130" fill={colors.white} rx="10" stroke={colors.ocean} strokeWidth="2" />
      <text x="90" y="25" textAnchor="middle" fill={colors.secondary} fontSize="12" fontWeight="bold">Example 1</text>
      <text x="90" y="45" textAnchor="middle" fill={colors.slate} fontSize="10">Minor speeding in crash</text>
      
      {/* Fault indicator */}
      <rect x="20" y="60" width="140" height="12" fill="#e2e8f0" rx="6" />
      <rect x="20" y="60" width="14" height="12" fill={colors.ocean} rx="6" />
      <text x="90" y="88" textAnchor="middle" fill={colors.ocean} fontSize="11" fontWeight="bold">10% Your Fault</text>
      
      {/* Recovery */}
      <text x="90" y="108" textAnchor="middle" fill={colors.slate} fontSize="10">$100K damages →</text>
      <text x="90" y="123" textAnchor="middle" fill={colors.ocean} fontSize="14" fontWeight="bold">$90,000 recovery</text>
    </g>
    
    {/* Example 2: 30% fault */}
    <g transform="translate(260, 90)">
      <rect x="0" y="0" width="180" height="130" fill={colors.white} rx="10" stroke="#f59e0b" strokeWidth="2" />
      <text x="90" y="25" textAnchor="middle" fill={colors.secondary} fontSize="12" fontWeight="bold">Example 2</text>
      <text x="90" y="45" textAnchor="middle" fill={colors.slate} fontSize="10">Pedestrian jaywalking</text>
      
      {/* Fault indicator */}
      <rect x="20" y="60" width="140" height="12" fill="#e2e8f0" rx="6" />
      <rect x="20" y="60" width="42" height="12" fill="#f59e0b" rx="6" />
      <text x="90" y="88" textAnchor="middle" fill="#f59e0b" fontSize="11" fontWeight="bold">30% Your Fault</text>
      
      {/* Recovery */}
      <text x="90" y="108" textAnchor="middle" fill={colors.slate} fontSize="10">$80K damages →</text>
      <text x="90" y="123" textAnchor="middle" fill="#f59e0b" fontSize="14" fontWeight="bold">$56,000 recovery</text>
    </g>
    
    {/* Example 3: 80% fault */}
    <g transform="translate(470, 90)">
      <rect x="0" y="0" width="180" height="130" fill={colors.white} rx="10" stroke="#ef4444" strokeWidth="2" />
      <text x="90" y="25" textAnchor="middle" fill={colors.secondary} fontSize="12" fontWeight="bold">Example 3</text>
      <text x="90" y="45" textAnchor="middle" fill={colors.slate} fontSize="10">Running red light</text>
      
      {/* Fault indicator */}
      <rect x="20" y="60" width="140" height="12" fill="#e2e8f0" rx="6" />
      <rect x="20" y="60" width="112" height="12" fill="#ef4444" rx="6" />
      <text x="90" y="88" textAnchor="middle" fill="#ef4444" fontSize="11" fontWeight="bold">80% Your Fault</text>
      
      {/* Recovery */}
      <text x="90" y="108" textAnchor="middle" fill={colors.slate} fontSize="10">$200K damages →</text>
      <text x="90" y="123" textAnchor="middle" fill="#ef4444" fontSize="14" fontWeight="bold">$40,000 recovery</text>
    </g>
    
    {/* Formula explanation */}
    <g transform="translate(350, 250)">
      <rect x="-280" y="0" width="560" height="70" fill={colors.white} rx="12" stroke={colors.slateLight} strokeWidth="1" />
      <text x="0" y="28" textAnchor="middle" fill={colors.secondary} fontSize="14" fontWeight="bold">
        The Formula: Total Damages × (100% - Your Fault %) = Your Recovery
      </text>
      <text x="0" y="52" textAnchor="middle" fill={colors.slate} fontSize="12">
        Example: $100,000 × (100% - 10%) = $100,000 × 90% = $90,000
      </text>
    </g>
    
    {/* Key point */}
    <g transform="translate(350, 350)">
      <rect x="-300" y="0" width="600" height="50" fill={colors.ocean} rx="25" />
      <text x="0" y="30" textAnchor="middle" fill={colors.white} fontSize="13" fontWeight="bold">
        Washington's "Pure" System: You can recover even at 99% fault (you'd get 1%)
      </text>
    </g>
  </svg>
);

// ============================================
// 30. PURE VS MODIFIED COMPARATIVE FAULT
// Washington vs other states
// ============================================
export const PureVsModifiedComparativeFault = ({ className = '' }) => (
  <svg 
    viewBox="0 0 700 380" 
    className={`w-full max-w-3xl mx-auto ${className}`}
    role="img"
    aria-label="Comparison of pure vs modified comparative fault systems"
  >
    {/* Background */}
    <rect width="700" height="380" fill={colors.rose50} rx="16" />
    
    {/* Title */}
    <text x="350" y="35" textAnchor="middle" fill={colors.secondary} fontSize="18" fontWeight="bold">
      Washington vs. Other States: Comparative Fault Systems
    </text>
    
    {/* Washington - Pure System */}
    <g transform="translate(190, 70)">
      <rect x="-150" y="0" width="300" height="250" fill={colors.white} rx="12" stroke={colors.ocean} strokeWidth="3" />
      <rect x="-150" y="0" width="300" height="45" fill={colors.ocean} rx="12" />
      <rect x="-150" y="35" width="300" height="10" fill={colors.white} />
      <text x="0" y="28" textAnchor="middle" fill={colors.white} fontSize="14" fontWeight="bold">WASHINGTON</text>
      <text x="0" y="70" textAnchor="middle" fill={colors.ocean} fontSize="16" fontWeight="bold">Pure Comparative Fault</text>
      
      <text x="-130" y="100" fill={colors.ocean} fontSize="14">✓</text>
      <text x="-110" y="100" fill={colors.secondary} fontSize="11">Recover at ANY fault level</text>
      
      <text x="-130" y="125" fill={colors.ocean} fontSize="14">✓</text>
      <text x="-110" y="125" fill={colors.secondary} fontSize="11">Even 99% at fault = 1% recovery</text>
      
      <text x="-130" y="150" fill={colors.ocean} fontSize="14">✓</text>
      <text x="-110" y="150" fill={colors.secondary} fontSize="11">More fair for accident victims</text>
      
      <text x="-130" y="175" fill={colors.ocean} fontSize="14">✓</text>
      <text x="-110" y="175" fill={colors.secondary} fontSize="11">Adopted since 1973</text>
      
      {/* Example */}
      <rect x="-130" y="195" width="260" height="45" fill={colors.rose50} rx="8" />
      <text x="0" y="215" textAnchor="middle" fill={colors.slate} fontSize="10">Example: 80% your fault, $100K damages</text>
      <text x="0" y="230" textAnchor="middle" fill={colors.ocean} fontSize="12" fontWeight="bold">You recover: $20,000</text>
    </g>
    
    {/* Other States - Modified System */}
    <g transform="translate(510, 70)">
      <rect x="-150" y="0" width="300" height="250" fill={colors.white} rx="12" stroke={colors.slateLight} strokeWidth="2" />
      <rect x="-150" y="0" width="300" height="45" fill={colors.slateLight} rx="12" />
      <rect x="-150" y="35" width="300" height="10" fill={colors.white} />
      <text x="0" y="28" textAnchor="middle" fill={colors.white} fontSize="14" fontWeight="bold">MANY OTHER STATES</text>
      <text x="0" y="70" textAnchor="middle" fill={colors.slate} fontSize="16" fontWeight="bold">Modified Comparative Fault</text>
      
      <text x="-130" y="100" fill="#ef4444" fontSize="14">✗</text>
      <text x="-110" y="100" fill={colors.secondary} fontSize="11">50% or 51% threshold</text>
      
      <text x="-130" y="125" fill="#ef4444" fontSize="14">✗</text>
      <text x="-110" y="125" fill={colors.secondary} fontSize="11">Over threshold = $0 recovery</text>
      
      <text x="-130" y="150" fill="#ef4444" fontSize="14">✗</text>
      <text x="-110" y="150" fill={colors.secondary} fontSize="11">Harsh on shared-fault accidents</text>
      
      <text x="-130" y="175" fill={colors.ocean} fontSize="14">✓</text>
      <text x="-110" y="175" fill={colors.secondary} fontSize="11">Under threshold still recovers</text>
      
      {/* Example */}
      <rect x="-130" y="195" width="260" height="45" fill="#fef2f2" rx="8" />
      <text x="0" y="215" textAnchor="middle" fill={colors.slate} fontSize="10">Example: 51% your fault, $100K damages</text>
      <text x="0" y="230" textAnchor="middle" fill="#ef4444" fontSize="12" fontWeight="bold">You recover: $0 (barred)</text>
    </g>
    
    {/* Bottom note */}
    <text x="350" y="355" textAnchor="middle" fill={colors.secondary} fontSize="12" fontWeight="bold">
      Washington's system is more favorable to injury victims with shared fault
    </text>
  </svg>
);

// ============================================
// 31. COMPARATIVE FAULT PROTECTION STEPS
// Steps to minimize fault reductions
// ============================================
export const ComparativeFaultProtectionSteps = ({ className = '' }) => (
  <svg 
    viewBox="0 0 700 450" 
    className={`w-full max-w-3xl mx-auto ${className}`}
    role="img"
    aria-label="Steps to protect your claim from unfair fault reductions"
  >
    {/* Background */}
    <rect width="700" height="450" fill={colors.rose50} rx="16" />
    
    {/* Title */}
    <text x="350" y="35" textAnchor="middle" fill={colors.secondary} fontSize="18" fontWeight="bold">
      6 Steps to Minimize Your Fault Percentage
    </text>
    <text x="350" y="55" textAnchor="middle" fill={colors.slate} fontSize="12">
      Protect your recovery from unfair reductions
    </text>
    
    {/* Step 1 */}
    <g transform="translate(120, 95)">
      <rect x="-90" y="-25" width="180" height="95" fill={colors.white} rx="10" stroke={colors.ocean} strokeWidth="2" />
      <circle cx="-60" cy="0" r="20" fill={colors.ocean} />
      <text x="-60" y="6" textAnchor="middle" fill={colors.white} fontSize="14" fontWeight="bold">1</text>
      <text x="15" y="-5" textAnchor="middle" fill={colors.secondary} fontSize="11" fontWeight="bold">Document</text>
      <text x="15" y="10" textAnchor="middle" fill={colors.secondary} fontSize="11" fontWeight="bold">Everything</text>
      <text x="0" y="35" textAnchor="middle" fill={colors.slate} fontSize="9">Photos, witnesses,</text>
      <text x="0" y="48" textAnchor="middle" fill={colors.slate} fontSize="9">police report</text>
    </g>
    
    {/* Step 2 */}
    <g transform="translate(350, 95)">
      <rect x="-90" y="-25" width="180" height="95" fill={colors.white} rx="10" stroke={colors.ocean} strokeWidth="2" />
      <circle cx="-60" cy="0" r="20" fill={colors.ocean} />
      <text x="-60" y="6" textAnchor="middle" fill={colors.white} fontSize="14" fontWeight="bold">2</text>
      <text x="15" y="-5" textAnchor="middle" fill={colors.secondary} fontSize="11" fontWeight="bold">Avoid Early</text>
      <text x="15" y="10" textAnchor="middle" fill={colors.secondary} fontSize="11" fontWeight="bold">Statements</text>
      <text x="0" y="35" textAnchor="middle" fill={colors.slate} fontSize="9">Don't talk to their</text>
      <text x="0" y="48" textAnchor="middle" fill={colors.slate} fontSize="9">insurer without advice</text>
    </g>
    
    {/* Step 3 */}
    <g transform="translate(580, 95)">
      <rect x="-90" y="-25" width="180" height="95" fill={colors.white} rx="10" stroke={colors.ocean} strokeWidth="2" />
      <circle cx="-60" cy="0" r="20" fill={colors.ocean} />
      <text x="-60" y="6" textAnchor="middle" fill={colors.white} fontSize="14" fontWeight="bold">3</text>
      <text x="15" y="-5" textAnchor="middle" fill={colors.secondary} fontSize="11" fontWeight="bold">Don't Admit</text>
      <text x="15" y="10" textAnchor="middle" fill={colors.secondary} fontSize="11" fontWeight="bold">Fault</text>
      <text x="0" y="35" textAnchor="middle" fill={colors.slate} fontSize="9">Even "sorry" can</text>
      <text x="0" y="48" textAnchor="middle" fill={colors.slate} fontSize="9">be used against you</text>
    </g>
    
    {/* Step 4 */}
    <g transform="translate(120, 220)">
      <rect x="-90" y="-25" width="180" height="95" fill={colors.white} rx="10" stroke={colors.ocean} strokeWidth="2" />
      <circle cx="-60" cy="0" r="20" fill={colors.ocean} />
      <text x="-60" y="6" textAnchor="middle" fill={colors.white} fontSize="14" fontWeight="bold">4</text>
      <text x="15" y="-5" textAnchor="middle" fill={colors.secondary} fontSize="11" fontWeight="bold">Follow Medical</text>
      <text x="15" y="10" textAnchor="middle" fill={colors.secondary} fontSize="11" fontWeight="bold">Advice</text>
      <text x="0" y="35" textAnchor="middle" fill={colors.slate} fontSize="9">Gaps in treatment =</text>
      <text x="0" y="48" textAnchor="middle" fill={colors.slate} fontSize="9">fault arguments</text>
    </g>
    
    {/* Step 5 */}
    <g transform="translate(350, 220)">
      <rect x="-90" y="-25" width="180" height="95" fill={colors.white} rx="10" stroke={colors.primary} strokeWidth="3" />
      <circle cx="-60" cy="0" r="20" fill={colors.primary} />
      <text x="-60" y="6" textAnchor="middle" fill={colors.white} fontSize="14" fontWeight="bold">5</text>
      <text x="15" y="-5" textAnchor="middle" fill={colors.secondary} fontSize="11" fontWeight="bold">Get an Attorney</text>
      <text x="15" y="10" textAnchor="middle" fill={colors.secondary} fontSize="11" fontWeight="bold">Early</text>
      <text x="0" y="35" textAnchor="middle" fill={colors.slate} fontSize="9">Counter fault claims,</text>
      <text x="0" y="48" textAnchor="middle" fill={colors.slate} fontSize="9">maximize recovery</text>
    </g>
    
    {/* Step 6 */}
    <g transform="translate(580, 220)">
      <rect x="-90" y="-25" width="180" height="95" fill={colors.white} rx="10" stroke={colors.ocean} strokeWidth="2" />
      <circle cx="-60" cy="0" r="20" fill={colors.ocean} />
      <text x="-60" y="6" textAnchor="middle" fill={colors.white} fontSize="14" fontWeight="bold">6</text>
      <text x="15" y="-5" textAnchor="middle" fill={colors.secondary} fontSize="11" fontWeight="bold">Preserve</text>
      <text x="15" y="10" textAnchor="middle" fill={colors.secondary} fontSize="11" fontWeight="bold">Evidence</text>
      <text x="0" y="35" textAnchor="middle" fill={colors.slate} fontSize="9">Dashcam, phone records,</text>
      <text x="0" y="48" textAnchor="middle" fill={colors.slate} fontSize="9">expert analysis</text>
    </g>
    
    {/* Warning box */}
    <g transform="translate(350, 355)">
      <rect x="-280" y="-25" width="560" height="70" fill="#fef2f2" rx="10" stroke="#ef4444" strokeWidth="2" />
      <text x="0" y="0" textAnchor="middle" fill="#ef4444" fontSize="13" fontWeight="bold">
        ⚠️ Insurance Tactics to Watch For
      </text>
      <text x="0" y="22" textAnchor="middle" fill={colors.secondary} fontSize="11">
        Adjusters often claim "failure to keep lookout," "speeding," or "no seatbelt"
      </text>
      <text x="0" y="38" textAnchor="middle" fill={colors.secondary} fontSize="11">
        to inflate your fault percentage and reduce your settlement
      </text>
    </g>
  </svg>
);

// ============================================
// 23. FIREFIGHTER INJURY TYPES
// Common on-duty injuries for fire/rescue
// ============================================
export const FirefighterInjuryTypes = ({ className = '' }) => (
  <svg 
    viewBox="0 0 700 400" 
    className={`w-full max-w-3xl mx-auto ${className}`}
    role="img"
    aria-label="Common firefighter on-duty injuries and third-party claim scenarios"
  >
    {/* Background */}
    <rect width="700" height="400" fill="#fef2f2" rx="16" />
    
    {/* Title */}
    <text x="350" y="35" textAnchor="middle" fill={colors.secondary} fontSize="18" fontWeight="bold">
      Common Firefighter Third-Party Injury Scenarios
    </text>
    <text x="350" y="55" textAnchor="middle" fill={colors.slate} fontSize="12">
      These injuries may qualify for civil claims beyond workers' comp
    </text>
    
    {/* Grid of injury scenarios */}
    <g transform="translate(130, 140)">
      <rect x="-100" y="-55" width="200" height="110" fill={colors.white} rx="12" stroke={fireColors.fireRed} strokeWidth="2" />
      <text x="0" y="-25" textAnchor="middle" fill={fireColors.fireRed} fontSize="28">🚗</text>
      <text x="0" y="5" textAnchor="middle" fill={colors.secondary} fontSize="13" fontWeight="bold">Vehicle Collisions</text>
      <text x="0" y="25" textAnchor="middle" fill={colors.slate} fontSize="10">Fire truck struck by</text>
      <text x="0" y="38" textAnchor="middle" fill={colors.slate} fontSize="10">negligent driver en route</text>
    </g>
    
    <g transform="translate(350, 140)">
      <rect x="-100" y="-55" width="200" height="110" fill={colors.white} rx="12" stroke={fireColors.fireRed} strokeWidth="2" />
      <text x="0" y="-25" textAnchor="middle" fill={fireColors.fireRed} fontSize="28">⚠️</text>
      <text x="0" y="5" textAnchor="middle" fill={colors.secondary} fontSize="13" fontWeight="bold">Roadside Strikes</text>
      <text x="0" y="25" textAnchor="middle" fill={colors.slate} fontSize="10">Hit while working at</text>
      <text x="0" y="38" textAnchor="middle" fill={colors.slate} fontSize="10">accident scene</text>
    </g>
    
    <g transform="translate(570, 140)">
      <rect x="-100" y="-55" width="200" height="110" fill={colors.white} rx="12" stroke={fireColors.fireRed} strokeWidth="2" />
      <text x="0" y="-25" textAnchor="middle" fill={fireColors.fireRed} fontSize="28">🔧</text>
      <text x="0" y="5" textAnchor="middle" fill={colors.secondary} fontSize="13" fontWeight="bold">Equipment Failure</text>
      <text x="0" y="25" textAnchor="middle" fill={colors.slate} fontSize="10">Defective gear from</text>
      <text x="0" y="38" textAnchor="middle" fill={colors.slate} fontSize="10">third-party manufacturer</text>
    </g>
    
    <g transform="translate(190, 280)">
      <rect x="-100" y="-55" width="200" height="110" fill={colors.white} rx="12" stroke={fireColors.fireOrange} strokeWidth="2" />
      <text x="0" y="-25" textAnchor="middle" fill={fireColors.fireOrange} fontSize="28">🏗️</text>
      <text x="0" y="5" textAnchor="middle" fill={colors.secondary} fontSize="13" fontWeight="bold">Structural Hazards</text>
      <text x="0" y="25" textAnchor="middle" fill={colors.slate} fontSize="10">Code violations or negligent</text>
      <text x="0" y="38" textAnchor="middle" fill={colors.slate} fontSize="10">property conditions</text>
    </g>
    
    <g transform="translate(510, 280)">
      <rect x="-100" y="-55" width="200" height="110" fill={colors.white} rx="12" stroke={fireColors.fireOrange} strokeWidth="2" />
      <text x="0" y="-25" textAnchor="middle" fill={fireColors.fireOrange} fontSize="28">☠️</text>
      <text x="0" y="5" textAnchor="middle" fill={colors.secondary} fontSize="13" fontWeight="bold">Toxic Exposure</text>
      <text x="0" y="25" textAnchor="middle" fill={colors.slate} fontSize="10">Chemical release due to</text>
      <text x="0" y="38" textAnchor="middle" fill={colors.slate} fontSize="10">third-party negligence</text>
    </g>
    
    {/* Bottom callout */}
    <text x="350" y="380" textAnchor="middle" fill={fireColors.fireRed} fontSize="12" fontWeight="bold">
      Third-party = someone other than your employer caused your injury
    </text>
  </svg>
);

// ============================================
// 24. WORKERS COMP VS CIVIL CLAIM COMPARISON
// Shows what each type of claim covers
// ============================================
export const WorkersCompVsCivilClaim = ({ className = '' }) => (
  <svg 
    viewBox="0 0 700 380" 
    className={`w-full max-w-3xl mx-auto ${className}`}
    role="img"
    aria-label="Comparison of workers compensation vs civil claim compensation"
  >
    {/* Background */}
    <rect width="700" height="380" fill={colors.rose50} rx="16" />
    
    {/* Title */}
    <text x="350" y="35" textAnchor="middle" fill={colors.secondary} fontSize="18" fontWeight="bold">
      Workers' Comp vs. Third-Party Civil Claim
    </text>
    
    {/* Workers' Comp Column */}
    <g transform="translate(190, 60)">
      <rect x="-150" y="0" width="300" height="280" fill={colors.white} rx="12" stroke={colors.slateLight} strokeWidth="2" />
      <rect x="-150" y="0" width="300" height="50" fill={colors.slateLight} rx="12" />
      <rect x="-150" y="40" width="300" height="10" fill={colors.white} />
      <text x="0" y="30" textAnchor="middle" fill={colors.white} fontSize="14" fontWeight="bold">WORKERS' COMP ONLY</text>
      
      <text x="-130" y="75" fill={colors.ocean} fontSize="16">✓</text>
      <text x="-110" y="75" fill={colors.secondary} fontSize="12">Medical expenses</text>
      
      <text x="-130" y="105" fill={colors.ocean} fontSize="16">✓</text>
      <text x="-110" y="105" fill={colors.secondary} fontSize="12">Partial wage replacement (60-75%)</text>
      
      <text x="-130" y="135" fill={colors.ocean} fontSize="16">✓</text>
      <text x="-110" y="135" fill={colors.secondary} fontSize="12">Vocational rehabilitation</text>
      
      <text x="-130" y="175" fill="#ef4444" fontSize="16">✗</text>
      <text x="-110" y="175" fill={colors.slate} fontSize="12">Pain and suffering</text>
      
      <text x="-130" y="205" fill="#ef4444" fontSize="16">✗</text>
      <text x="-110" y="205" fill={colors.slate} fontSize="12">Full lost wages</text>
      
      <text x="-130" y="235" fill="#ef4444" fontSize="16">✗</text>
      <text x="-110" y="235" fill={colors.slate} fontSize="12">Future earning capacity</text>
      
      <text x="-130" y="265" fill="#ef4444" fontSize="16">✗</text>
      <text x="-110" y="265" fill={colors.slate} fontSize="12">Loss of enjoyment of life</text>
    </g>
    
    {/* Civil Claim Column */}
    <g transform="translate(510, 60)">
      <rect x="-150" y="0" width="300" height="280" fill={colors.white} rx="12" stroke={colors.ocean} strokeWidth="3" />
      <rect x="-150" y="0" width="300" height="50" fill={colors.ocean} rx="12" />
      <rect x="-150" y="40" width="300" height="10" fill={colors.white} />
      <text x="0" y="30" textAnchor="middle" fill={colors.white} fontSize="14" fontWeight="bold">+ THIRD-PARTY CIVIL CLAIM</text>
      
      <text x="-130" y="75" fill={colors.ocean} fontSize="16">✓</text>
      <text x="-110" y="75" fill={colors.secondary} fontSize="12">All medical expenses</text>
      
      <text x="-130" y="105" fill={colors.ocean} fontSize="16">✓</text>
      <text x="-110" y="105" fill={colors.secondary} fontSize="12" fontWeight="bold">100% of lost wages</text>
      
      <text x="-130" y="135" fill={colors.ocean} fontSize="16">✓</text>
      <text x="-110" y="135" fill={colors.secondary} fontSize="12" fontWeight="bold">Pain and suffering</text>
      
      <text x="-130" y="165" fill={colors.ocean} fontSize="16">✓</text>
      <text x="-110" y="165" fill={colors.secondary} fontSize="12" fontWeight="bold">Future earning capacity</text>
      
      <text x="-130" y="195" fill={colors.ocean} fontSize="16">✓</text>
      <text x="-110" y="195" fill={colors.secondary} fontSize="12" fontWeight="bold">Loss of enjoyment of life</text>
      
      <text x="-130" y="225" fill={colors.ocean} fontSize="16">✓</text>
      <text x="-110" y="225" fill={colors.secondary} fontSize="12" fontWeight="bold">Emotional distress</text>
      
      <text x="-130" y="255" fill={colors.ocean} fontSize="16">✓</text>
      <text x="-110" y="255" fill={colors.secondary} fontSize="12" fontWeight="bold">Loss of consortium</text>
    </g>
    
    {/* VS Badge */}
    <g transform="translate(350, 200)">
      <circle cx="0" cy="0" r="25" fill={colors.secondary} />
      <text x="0" y="6" textAnchor="middle" fill={colors.white} fontSize="14" fontWeight="bold">VS</text>
    </g>
    
    {/* Bottom note */}
    <text x="350" y="365" textAnchor="middle" fill={colors.secondary} fontSize="12" fontWeight="bold">
      You can pursue BOTH simultaneously when a third party caused your injury
    </text>
  </svg>
);

// EMS-specific colors
const emsColors = {
  emsGreen: '#059669',
  emsGreenLight: '#d1fae5',
};

// ============================================
// 25. EMS INJURY SCENARIOS
// Common paramedic/EMT injury situations
// ============================================
export const EMSInjuryScenarios = ({ className = '' }) => (
  <svg 
    viewBox="0 0 700 400" 
    className={`w-full max-w-3xl mx-auto ${className}`}
    role="img"
    aria-label="Common EMS worker injury scenarios qualifying for third-party claims"
  >
    {/* Background */}
    <rect width="700" height="400" fill={emsColors.emsGreenLight} rx="16" />
    
    {/* Title */}
    <text x="350" y="35" textAnchor="middle" fill={colors.secondary} fontSize="18" fontWeight="bold">
      When EMS Workers Can File Third-Party Claims
    </text>
    <text x="350" y="55" textAnchor="middle" fill={colors.slate} fontSize="12">
      Paramedics, EMTs, and medical transport professionals
    </text>
    
    {/* Scenario boxes */}
    <g transform="translate(130, 145)">
      <rect x="-100" y="-60" width="200" height="120" fill={colors.white} rx="12" stroke={emsColors.emsGreen} strokeWidth="2" />
      <text x="0" y="-30" textAnchor="middle" fill={emsColors.emsGreen} fontSize="28">🚑</text>
      <text x="0" y="0" textAnchor="middle" fill={colors.secondary} fontSize="12" fontWeight="bold">Ambulance Collision</text>
      <text x="0" y="20" textAnchor="middle" fill={colors.slate} fontSize="10">Another driver causes crash</text>
      <text x="0" y="35" textAnchor="middle" fill={colors.slate} fontSize="10">while responding to emergency</text>
    </g>
    
    <g transform="translate(350, 145)">
      <rect x="-100" y="-60" width="200" height="120" fill={colors.white} rx="12" stroke={emsColors.emsGreen} strokeWidth="2" />
      <text x="0" y="-30" textAnchor="middle" fill={emsColors.emsGreen} fontSize="28">🚗</text>
      <text x="0" y="0" textAnchor="middle" fill={colors.secondary} fontSize="12" fontWeight="bold">Roadside Strike</text>
      <text x="0" y="20" textAnchor="middle" fill={colors.slate} fontSize="10">Hit by passing vehicle while</text>
      <text x="0" y="35" textAnchor="middle" fill={colors.slate} fontSize="10">treating patients at scene</text>
    </g>
    
    <g transform="translate(570, 145)">
      <rect x="-100" y="-60" width="200" height="120" fill={colors.white} rx="12" stroke={emsColors.emsGreen} strokeWidth="2" />
      <text x="0" y="-30" textAnchor="middle" fill={emsColors.emsGreen} fontSize="28">🏢</text>
      <text x="0" y="0" textAnchor="middle" fill={colors.secondary} fontSize="12" fontWeight="bold">Premises Hazards</text>
      <text x="0" y="20" textAnchor="middle" fill={colors.slate} fontSize="10">Unsafe conditions at call</text>
      <text x="0" y="35" textAnchor="middle" fill={colors.slate} fontSize="10">location (property owner liable)</text>
    </g>
    
    <g transform="translate(190, 290)">
      <rect x="-100" y="-60" width="200" height="120" fill={colors.white} rx="12" stroke={emsColors.emsGreen} strokeWidth="2" />
      <text x="0" y="-30" textAnchor="middle" fill={emsColors.emsGreen} fontSize="28">⚙️</text>
      <text x="0" y="0" textAnchor="middle" fill={colors.secondary} fontSize="12" fontWeight="bold">Equipment Failure</text>
      <text x="0" y="20" textAnchor="middle" fill={colors.slate} fontSize="10">Defective stretcher, lift, or</text>
      <text x="0" y="35" textAnchor="middle" fill={colors.slate} fontSize="10">medical device causes injury</text>
    </g>
    
    <g transform="translate(510, 290)">
      <rect x="-100" y="-60" width="200" height="120" fill={colors.white} rx="12" stroke={emsColors.emsGreen} strokeWidth="2" />
      <text x="0" y="-30" textAnchor="middle" fill={emsColors.emsGreen} fontSize="28">☣️</text>
      <text x="0" y="0" textAnchor="middle" fill={colors.secondary} fontSize="12" fontWeight="bold">Hazmat Exposure</text>
      <text x="0" y="20" textAnchor="middle" fill={colors.slate} fontSize="10">Chemical spill or toxic exposure</text>
      <text x="0" y="35" textAnchor="middle" fill={colors.slate} fontSize="10">caused by third-party negligence</text>
    </g>
    
    {/* Bottom note */}
    <text x="350" y="380" textAnchor="middle" fill={emsColors.emsGreen} fontSize="12" fontWeight="bold">
      Key: The at-fault party is NOT your employer—it's a third party
    </text>
  </svg>
);

// WSDOT-specific colors
const wsdotColors = {
  wsdotOrange: '#d97706',
  wsdotOrangeLight: '#fef3c7',
};

// ============================================
// 26. WSDOT WORK ZONE HAZARDS
// Highway worker injury risks
// ============================================
export const WSDOTWorkZoneHazards = ({ className = '' }) => (
  <svg 
    viewBox="0 0 700 380" 
    className={`w-full max-w-3xl mx-auto ${className}`}
    role="img"
    aria-label="WSDOT and highway worker injury risks in work zones"
  >
    {/* Background */}
    <rect width="700" height="380" fill={wsdotColors.wsdotOrangeLight} rx="16" />
    
    {/* Title */}
    <text x="350" y="35" textAnchor="middle" fill={colors.secondary} fontSize="18" fontWeight="bold">
      WSDOT & Highway Worker Injury Risks
    </text>
    <text x="350" y="55" textAnchor="middle" fill={colors.slate} fontSize="12">
      When negligent drivers enter work zones, workers have civil claims
    </text>
    
    {/* Work zone diagram */}
    <rect x="50" y="90" width="600" height="80" fill="#374151" rx="4" />
    <text x="350" y="125" textAnchor="middle" fill={colors.white} fontSize="11">HIGHWAY</text>
    
    {/* Orange cones */}
    <polygon points="150,170 160,140 170,170" fill={wsdotColors.wsdotOrange} />
    <polygon points="250,170 260,140 270,170" fill={wsdotColors.wsdotOrange} />
    <polygon points="350,170 360,140 370,170" fill={wsdotColors.wsdotOrange} />
    <polygon points="450,170 460,140 470,170" fill={wsdotColors.wsdotOrange} />
    <polygon points="550,170 560,140 570,170" fill={wsdotColors.wsdotOrange} />
    
    {/* Work zone area */}
    <rect x="100" y="180" width="500" height="60" fill={wsdotColors.wsdotOrange} rx="4" opacity="0.3" />
    <text x="350" y="215" textAnchor="middle" fill={colors.secondary} fontSize="12" fontWeight="bold">⚠️ ACTIVE WORK ZONE</text>
    
    {/* Stats boxes */}
    <g transform="translate(130, 300)">
      <rect x="-100" y="-40" width="200" height="80" fill={colors.white} rx="10" stroke={wsdotColors.wsdotOrange} strokeWidth="2" />
      <text x="0" y="-10" textAnchor="middle" fill={wsdotColors.wsdotOrange} fontSize="24" fontWeight="bold">857</text>
      <text x="0" y="15" textAnchor="middle" fill={colors.secondary} fontSize="11">Work zone fatalities</text>
      <text x="0" y="30" textAnchor="middle" fill={colors.slate} fontSize="10">nationally per year (FHWA)</text>
    </g>
    
    <g transform="translate(350, 300)">
      <rect x="-100" y="-40" width="200" height="80" fill={colors.white} rx="10" stroke={wsdotColors.wsdotOrange} strokeWidth="2" />
      <text x="0" y="-10" textAnchor="middle" fill={wsdotColors.wsdotOrange} fontSize="24" fontWeight="bold">85%</text>
      <text x="0" y="15" textAnchor="middle" fill={colors.secondary} fontSize="11">Caused by motorist</text>
      <text x="0" y="30" textAnchor="middle" fill={colors.slate} fontSize="10">error or negligence</text>
    </g>
    
    <g transform="translate(570, 300)">
      <rect x="-100" y="-40" width="200" height="80" fill={colors.white} rx="10" stroke={colors.ocean} strokeWidth="2" />
      <text x="0" y="-10" textAnchor="middle" fill={colors.ocean} fontSize="24" fontWeight="bold">100%</text>
      <text x="0" y="15" textAnchor="middle" fill={colors.secondary} fontSize="11">Your right to sue</text>
      <text x="0" y="30" textAnchor="middle" fill={colors.slate} fontSize="10">negligent drivers</text>
    </g>
    
    {/* Bottom note */}
    <text x="350" y="365" textAnchor="middle" fill={colors.secondary} fontSize="11">
      Civil claims against at-fault drivers = full compensation beyond workers' comp
    </text>
  </svg>
);

// Public Works colors
const publicWorksColors = {
  pwGray: '#475569',
  pwGrayLight: '#f1f5f9',
};

// ============================================
// 27. PUBLIC WORKS INJURY CHECKLIST
// Steps for county/city workers after injury
// ============================================
export const PublicWorksInjuryChecklist = ({ className = '' }) => (
  <svg 
    viewBox="0 0 700 420" 
    className={`w-full max-w-3xl mx-auto ${className}`}
    role="img"
    aria-label="Step-by-step checklist for injured public works employees"
  >
    {/* Background */}
    <rect width="700" height="420" fill={publicWorksColors.pwGrayLight} rx="16" />
    
    {/* Title */}
    <text x="350" y="35" textAnchor="middle" fill={colors.secondary} fontSize="18" fontWeight="bold">
      Injured on the Job? Your Action Checklist
    </text>
    <text x="350" y="55" textAnchor="middle" fill={colors.slate} fontSize="12">
      For county road crews, city maintenance, utility workers & infrastructure teams
    </text>
    
    {/* Checklist items */}
    <g transform="translate(350, 110)">
      <rect x="-300" y="-30" width="600" height="60" fill={colors.white} rx="8" stroke={publicWorksColors.pwGray} strokeWidth="1" />
      <circle cx="-260" cy="0" r="18" fill={colors.ocean} />
      <text x="-260" y="6" textAnchor="middle" fill={colors.white} fontSize="14" fontWeight="bold">1</text>
      <text x="-220" y="-5" fill={colors.secondary} fontSize="13" fontWeight="bold">Report to your supervisor immediately</text>
      <text x="-220" y="15" fill={colors.slate} fontSize="11">Document what happened, where, when, and any witnesses</text>
    </g>
    
    <g transform="translate(350, 180)">
      <rect x="-300" y="-30" width="600" height="60" fill={colors.white} rx="8" stroke={publicWorksColors.pwGray} strokeWidth="1" />
      <circle cx="-260" cy="0" r="18" fill={colors.ocean} />
      <text x="-260" y="6" textAnchor="middle" fill={colors.white} fontSize="14" fontWeight="bold">2</text>
      <text x="-220" y="-5" fill={colors.secondary} fontSize="13" fontWeight="bold">Get medical attention—document everything</text>
      <text x="-220" y="15" fill={colors.slate} fontSize="11">Tell doctors HOW you were injured (third party vs. workplace)</text>
    </g>
    
    <g transform="translate(350, 250)">
      <rect x="-300" y="-30" width="600" height="60" fill={colors.white} rx="8" stroke={publicWorksColors.pwGray} strokeWidth="1" />
      <circle cx="-260" cy="0" r="18" fill={colors.ocean} />
      <text x="-260" y="6" textAnchor="middle" fill={colors.white} fontSize="14" fontWeight="bold">3</text>
      <text x="-220" y="-5" fill={colors.secondary} fontSize="13" fontWeight="bold">File workers' comp claim with your agency</text>
      <text x="-220" y="15" fill={colors.slate} fontSize="11">This protects your immediate medical/wage benefits</text>
    </g>
    
    <g transform="translate(350, 320)">
      <rect x="-300" y="-30" width="600" height="60" fill={colors.white} rx="8" stroke={colors.primary} strokeWidth="2" />
      <circle cx="-260" cy="0" r="18" fill={colors.primary} />
      <text x="-260" y="6" textAnchor="middle" fill={colors.white} fontSize="14" fontWeight="bold">4</text>
      <text x="-220" y="-5" fill={colors.secondary} fontSize="13" fontWeight="bold">Contact an attorney about third-party claims</text>
      <text x="-220" y="15" fill={colors.slate} fontSize="11">If someone ELSE caused your injury, you may have additional claims</text>
    </g>
    
    {/* Important callout */}
    <g transform="translate(350, 390)">
      <rect x="-280" y="-20" width="560" height="40" fill={colors.secondary} rx="20" />
      <text x="0" y="5" textAnchor="middle" fill={colors.white} fontSize="12" fontWeight="bold">
        💡 Workers' comp and civil claims are SEPARATE—you can pursue both!
      </text>
    </g>
  </svg>
);

// ============================================
// 28. PUBLIC SAFETY DECISION TREE (General)
// Do you have a third-party claim?
// ============================================
export const PublicSafetyClaimDecisionTree = ({ className = '' }) => (
  <svg 
    viewBox="0 0 700 450" 
    className={`w-full max-w-3xl mx-auto ${className}`}
    role="img"
    aria-label="Decision tree to determine if public safety workers have third-party claims"
  >
    {/* Background */}
    <rect width="700" height="450" fill={colors.rose50} rx="16" />
    
    {/* Title */}
    <text x="350" y="35" textAnchor="middle" fill={colors.secondary} fontSize="18" fontWeight="bold">
      Do You Have a Third-Party Claim?
    </text>
    <text x="350" y="55" textAnchor="middle" fill={colors.slate} fontSize="12">
      For all public safety workers: police, fire, EMS, WSDOT, public works
    </text>
    
    {/* Start node */}
    <g transform="translate(350, 100)">
      <rect x="-120" y="-25" width="240" height="50" fill={colors.secondary} rx="25" />
      <text x="0" y="5" textAnchor="middle" fill={colors.white} fontSize="13" fontWeight="bold">Were you injured on duty?</text>
    </g>
    
    {/* First decision */}
    <line x1="350" y1="125" x2="350" y2="155" stroke={colors.secondary} strokeWidth="2" />
    <g transform="translate(350, 180)">
      <rect x="-140" y="-25" width="280" height="50" fill={colors.white} rx="8" stroke={colors.ocean} strokeWidth="2" />
      <text x="0" y="5" textAnchor="middle" fill={colors.secondary} fontSize="12" fontWeight="bold">Did someone OTHER than your employer</text>
      <text x="0" y="20" textAnchor="middle" fill={colors.secondary} fontSize="12" fontWeight="bold">cause or contribute to your injury?</text>
    </g>
    
    {/* YES path */}
    <line x1="210" y1="180" x2="130" y2="180" stroke={colors.ocean} strokeWidth="2" />
    <line x1="130" y1="180" x2="130" y2="250" stroke={colors.ocean} strokeWidth="2" />
    <text x="155" y="175" fill={colors.ocean} fontSize="11" fontWeight="bold">YES</text>
    
    <g transform="translate(130, 290)">
      <rect x="-100" y="-35" width="200" height="70" fill={colors.ocean} rx="8" />
      <text x="0" y="-5" textAnchor="middle" fill={colors.white} fontSize="12" fontWeight="bold">✓ You likely have a</text>
      <text x="0" y="12" textAnchor="middle" fill={colors.white} fontSize="12" fontWeight="bold">THIRD-PARTY CLAIM</text>
    </g>
    
    {/* NO path */}
    <line x1="490" y1="180" x2="570" y2="180" stroke={colors.slateLight} strokeWidth="2" />
    <line x1="570" y1="180" x2="570" y2="250" stroke={colors.slateLight} strokeWidth="2" />
    <text x="540" y="175" fill={colors.slate} fontSize="11" fontWeight="bold">NO</text>
    
    <g transform="translate(570, 290)">
      <rect x="-100" y="-35" width="200" height="70" fill={colors.slateLight} rx="8" />
      <text x="0" y="-5" textAnchor="middle" fill={colors.white} fontSize="12" fontWeight="bold">Workers' comp only</text>
      <text x="0" y="12" textAnchor="middle" fill={colors.white} fontSize="11">(but still contact an attorney)</text>
    </g>
    
    {/* Examples under YES */}
    <g transform="translate(130, 380)">
      <text x="0" y="0" textAnchor="middle" fill={colors.secondary} fontSize="11" fontWeight="bold">Examples:</text>
      <text x="0" y="18" textAnchor="middle" fill={colors.slate} fontSize="10">• Negligent driver hit you</text>
      <text x="0" y="33" textAnchor="middle" fill={colors.slate} fontSize="10">• Defective equipment</text>
      <text x="0" y="48" textAnchor="middle" fill={colors.slate} fontSize="10">• Property owner negligence</text>
    </g>
    
    {/* Bottom CTA */}
    <g transform="translate(350, 425)">
      <text x="0" y="0" textAnchor="middle" fill={colors.primary} fontSize="13" fontWeight="bold">
        Not sure? Call (425) 675-1224 for a free consultation
      </text>
    </g>
  </svg>
);

// Export all graphics
export default {
  CompensationComparisonChart,
  StatuteOfLimitationsTimeline,
  WhyStatutesExistGrid,
  ExceptionsFlowchart,
  CarAccidentStepsFlowchart,
  MaintenanceAndCureInfoGraphic,
  JonesActRightsDiagram,
  FirstResponderDecisionTree,
  DocumentsChecklistGraphic,
  CaseValueFactorsGraphic,
  ClaimsProcessTimeline,
  InsuranceVsAttorneyComparison,
  InsuranceAdjusterTacticsGraphic,
  AfterAccidentInsuranceSteps,
  WashingtonClaimsLawGraphic,
  CrosswalkLawDiagram,
  ComparativeNegligenceChart,
  PedestrianEvidenceChecklist,
  PedestrianClaimTimeline,
  SocialMediaRisksDiagram,
  SocialMediaTimelineImpact,
  SocialMediaProtectionSteps,
  ComparativeFaultExamplesChart,
  PureVsModifiedComparativeFault,
  ComparativeFaultProtectionSteps,
  FirefighterInjuryTypes,
  WorkersCompVsCivilClaim,
  EMSInjuryScenarios,
  WSDOTWorkZoneHazards,
  PublicWorksInjuryChecklist,
  PublicSafetyClaimDecisionTree,
};
